import { useNarrowViewport } from "./useNarrowViewport";
import ProductFactDialoguePanel from './ProductFactDialoguePanel';
import { isLegalDecisionKey, isLegalDecisionText, isProductQuestion, productDialogue } from './productFactDialogue';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { fetchSubdivisionPreview, fetchTargetConditions, requiresDirectProductAnswer } from "./subdivisionPreviewClient";
import { factLabel, humanizeCode, valueLabel } from "./proveFactLabels";
import { loadTreeFacts, loadUnverifiedTreeFacts, saveTreeFacts, TREE_FACTS_UPDATED_EVENT, treeFactKey } from "./treeFactStore";
import { fetchResearchFactProposals, hintQueriesFromResearch, stripClassificationTalk, researchDescriptionProposal } from "./subdivisionPreviewClient";
import {
  RESEARCH_UPDATED_EVENT, loadProductResearch, readGateState, researchEventKey,
} from "./ProductIdentityGate";
import { fetchExclusionNotes } from "./noteExclusions";
import { buildPrecedentGuide } from "./precedentGuide";
import { consensusRelation, precedentConsensus, precedentTitleConsensus } from "./precedentConsensus";
import { buildSearchHighlightTokens } from "./highlightTokens";
import { classifyTariffMatches, normalizeTariffName, tariffNameMatches } from "./tariffNameMatch";
import { shortHeadingTitle, useHeadingTitles } from "./headingTitles";
import DecisionConditions from "./DecisionConditions";
import BranchMapView from "./BranchMapView";
import { buildCandidateCompareModel, candidateHs4Hints, headingDetailToItems, nameViaOf, needsHeadingDetail, proposalLlmMap, readViewPref, signalHeadingHint, signalSubheadingFor, writeViewPref } from "./candidateCompareModel";
import TariffBranchTable from "./TariffBranchTable";
import { clearAskContext, publishAskContext, useAskFind, useAskFlash } from "./askContextStore";
import { useHistorySignal } from "./historySignalStore";
import { sourceCodes, sourceHeadings } from "./sourceCodes";
import { isLlmMapVisible } from "./verdictTab";
import { requestFindStep } from "./findMatch";
import { buildBranchMap, buildBranchTable } from "./branchMapModel";
import { fetchBranchMap, recordMapAdoption } from "./branchMapClient";
import CandidateChecklistFold from "./CandidateChecklistFold";

/** 호 상세 응답 캐시(호마다 1회). 표 보기가 후보 호 전부의 구조를 그릴 때 쓴다. */
const headingDetailCache = new Map();
export function resetHeadingDetailCache() { headingDetailCache.clear(); }

/** 미확정 사전 점검에서 웹검증 투영을 자동으로 한 번 돌릴지(사용자 설정, 기본 켬). */
export const RESEARCH_AUTO_PREF_KEY = "loginhs:research-facts-auto";
export function researchAutoEnabled() {
  try { return localStorage.getItem(RESEARCH_AUTO_PREF_KEY) !== "off"; } catch (_) { return true; }
}
function setResearchAutoEnabled(on) {
  try { localStorage.setItem(RESEARCH_AUTO_PREF_KEY, on ? "on" : "off"); } catch (_) { /* ignore */ }
}
const researchAutoDoneKey = (q) => `loginhs:research-facts-auto-done:${String(q || "").replace(/\s+/g, " ").trim().toLowerCase()}`;
function researchAutoDone(q) {
  try { return sessionStorage.getItem(researchAutoDoneKey(q)) === "1"; } catch (_) { return true; }
}
function markResearchAutoDone(q) {
  try { sessionStorage.setItem(researchAutoDoneKey(q), "1"); } catch (_) { /* ignore */ }
}
/** 게이트 캐시의 조사 결과를 투영·재계산에 쓸 한 덩어리 문장으로 만든다(정체 + 요약 + 출처 발췌). */
export function researchSummaryText(research) {
  if (!research) return "";
  const snippets = Array.isArray(research.sources)
    ? research.sources.map((s) => s?.snippet || s?.description || "").filter(Boolean).join("\n")
    : "";
  // 세번·분류 추정 문장은 뺀다(트리 문언·사실 제안 모두). 남는 게 없으면 조사 결과가 없는 것으로 본다.
  return stripClassificationTalk([research.identity, research.summary, snippets].filter(Boolean).join("\n"));
}

/**
 * 호 선택지·사실 칩의 표시 라벨. "4202 · 트렁크·슈트 케이스·…(호 문언 전체)"처럼 긴 라벨은 앞부분만 보이고
 * 전체 문언은 title(툴팁)로 넘긴다. 코드 접두(4202 · )는 항상 남긴다.
 */
export function shortLabel(label, max = 56) {
  const text = String(label ?? "");
  if (text.length <= max) return text;
  const cut = text.slice(0, max - 1);
  // 구분자(·, ,, 공백) 직전에서 끊어 단어가 반토막 나지 않게 한다.
  const breakAt = Math.max(cut.lastIndexOf("·"), cut.lastIndexOf(","), cut.lastIndexOf(" "));
  const head = breakAt > Math.floor(max * 0.6) ? cut.slice(0, breakAt) : cut;
  return `${head.replace(/[\s·,]+$/, "")}…`;
}

export function formatSubdivisionHsk(value) {
  const digits = String(value || "").replace(/[^0-9]/g, "");
  if (digits.length === 6) return `${digits.slice(0, 4)}.${digits.slice(4)}`;
  if (digits.length !== 10) return String(value || "-");
  return `${digits.slice(0, 4)}.${digits.slice(4, 6)}-${digits.slice(6)}`;
}

// 상태 색은 두 가지만 쓴다. 계산이 닫힌 상태는 파랑, 나머지(사실 필요·후보 여러 개·범위 밖)는 회색.
// 주황·초록 같은 경고색은 쓰지 않는다(사용자 요청 2026-09-03: AI스럽지 않게).
const BLUE = { color: "#1d4ed8", background: "#eff6ff", border: "#93c5fd" };
const GREY = { color: "#475569", background: "var(--audit-bg-subtle)", border: "var(--audit-border-strong, #cdd1d8)" };
const GREEN = BLUE;
const AMBER = GREY;

function statusMeta(shadow) {
  if (shadow?.action === "supported" || shadow?.supported_hsk
      || (Array.isArray(shadow?.supported_hsks) && shadow.supported_hsks.length > 0)) {
    return { label: "결정표 후보 계산됨", ...GREEN };
  }
  return {
    label: shadow?.action === "ambiguous" ? "후보가 여러 개입니다" : "추가 사실 확인 필요",
    ...GREY,
  };
}

function treeStatusMeta(tree) {
  const dialogue = productDialogue(tree);
  if (dialogue.conditional_assessment) return { label: '조건부 검토 방향', ...GREY };
  const action = String(tree?.action || "");
  // 관측 분해가 류 주의 제외 목록을 '해당 없음'으로 가정해 계산한 결과 — 세번은 보이되 확정 표시는 하지 않는다(2026-09-24).
  if (tree?.provisional_legal_fact && String(tree?.action || "") !== "out_of_scope") {
    return { label: tree.reason === "observable_exclusion_check_pending" ? "조건부 · 제외 물품 확인 전" : tree.reason === "signal_heading_assumption_pending" ? "조건부 · 추천 호로 계산" : "조건부 · 확인 전", ...GREY };
  }
  if (action === "supported" || action === "supported_hs6" || tree?.supported_hsk) {
    return { label: "결정트리 후보 계산됨", ...GREEN };
  }
  if (action === "ambiguous") return { label: "후보가 여러 개입니다", ...AMBER };
  if (action === "out_of_scope") return { label: "이 결정트리 범위 밖", ...GREY };
  return { label: "추가 사실 확인 필요", ...GREY };
}

const digitsOf = (value) => String(value || "").replace(/[^0-9]/g, "");
const treeHs6Of = (tree) => digitsOf(tree?.supported_hs6);

/**
 * 배경 재계산 알림(2026-09-27) — 첫 결과가 나온 뒤 앞단 신호(관세율표 명칭·결정례·이력·LLM)가 도착하면 카드가 뒤에서 다시 계산해
 * 결과를 바꾼다. 그 사이·그 뒤에 무슨 일이 있었는지 카드 위에 한 줄로 밝힌다(말없이 화면이 바뀌지 않게).
 */
export default function SubdivisionPreviewCard(props) {
  const [bg, setBg] = useState(null);
  // 검색어가 바뀌면 옛 알림은 bg.q 가 달라 보이지 않는다(부모 효과가 자식보다 늦게 돌아 지우면 첫 알림이 사라진다).
  const shown = bg && bg.q === props.q ? bg : null;
  return (
    <>
      {shown && (
        <div data-testid="background-recompute" role="status" aria-live="polite"
          style={{ fontSize: 12, color: shown.state === "running" ? "#1d4ed8" : "var(--audit-text-muted)", padding: "4px 2px" }}>
          {shown.state === "running" ? "⟳ " : "✓ "}{shown.text}
        </div>
      )}
      <SubdivisionPreviewCardInner {...props} onBackgroundStatus={setBg} />
    </>
  );
}

function SubdivisionPreviewCardInner({
  q, preview, onOpenProve, productDetails, fetchImpl, tariffItems: tariffItemsProp, proposal = null, tariffEngine = null, onBackgroundStatus = () => {},
}) {
  // LLM이 읽은 법령어 품명(제안 신호 name_ko)으로 관세율표를 한 번 더 찾는다 — 사전에 없는 표기 변형(커피그라인드)을 이름 일치로 잇는 다리.
  // LLM은 이름만 제안하고, 세번은 서버의 명칭 비교(name_matches)가, 확정은 트리가 정한다(2026-09-15 사용자: "동의어를 계속 추가? LLM이 인식?" → 둘의 역할 분담).
  const llmName = llmNameOf(proposal, q);
  const llmLookup = useLlmNameLookup({ q, name: llmName, fetchImpl });
  const tariffItems = useMemo(() => mergeTariffItems(tariffItemsProp, llmLookup?.items), [tariffItemsProp, llmLookup]);
  // 관세율표 10자리 명칭이 검색어와 같은 세번(커피분쇄기 → 8509.80-1000). 트리 유무와 무관하게 먼저 보여 준다.
  // 글자가 달라도 검색 엔진이 동의어로 낱말 전부를 한 건에 맞췄으면(커피그라인더) 같은 일치로 본다. 둘 다 없으면 LLM 품명 경로.
  // LLM 이 읽은 품명으로 찾은 명칭 일치(예: 'HEAT SINK' → '방열기' → 7322.11 주철 방열기)는 LLM 신호일 뿐이다 —
  // 국내 결정례가 다른 호를 가리키면 결정례를 앞세우고 이 일치는 버린다(2026-09-27 사용자 사례: 결정례 7616.99-9090).
  const precedentByHsk = preview?.row?.precedent_hints?.by_hsk;
  const precedentHs4s = useMemo(() => new Set((Array.isArray(precedentByHsk) ? precedentByHsk : [])
    .filter((r) => Number(r?.count) > 0).map((r) => digitsOf(r.hsk).slice(0, 4)).filter((h) => h.length === 4)), [precedentByHsk]);
  // 관세율표 호·소호 명칭 일치는 원래 검색어로 찾았을 때만 출처로 쓴다(2026-09-29) — 긴 서술형은 낱말을 줄여 다시 찾는데(relaxed)
  // 그때 남는 '포장·물품' 같은 일반어가 자갈(2517)·장착구(8302) 같은 엉뚱한 호 명칭에 걸렸다(제2165호 60문항 실측). 오타 교정(typo)은 그대로.
  const trustedHeadingMatches = tariffEngine && (!tariffEngine.fallback || tariffEngine.fallback === "typo") ? (tariffEngine.headingMatches || []) : [];
  const tariffMatches = tariffNameMatches(q, tariffItems, { ...(tariffEngine || {}), llm: llmLookup })
    .filter((m) => !(nameViaOf(m) === "llm" && precedentHs4s.size > 0 && !precedentHs4s.has(digitsOf(m.hsk).slice(0, 4))));
  // 지도·표 이름의 노란 강조(44차): 검색어 낱말 + 검색 엔진이 실제로 맞춘 낱말(분절·동의어·오타 교정 토큰) + LLM 법령어 품명.
  // 찾기 칸과 같은 노랑이지만 찾기 1/N 계산에는 들어가지 않는다.
  const engineTokens = tariffEngine?.matchTokens;
  const queryTerms = useMemo(
    () => buildSearchHighlightTokens(q, [...(Array.isArray(engineTokens) ? engineTokens : []), ...(llmName ? [llmName] : [])]),
    [q, engineTokens, llmName],
  );
  // 카드 안에서 결정트리 질문에 답하면 같은 검색어의 사전 점검만 다시 계산한다.
  // 전체 검색 결과·기존 세번은 건드리지 않고, 검색어가 바뀌면 답과 재계산 결과를 버린다.
  const [answers, setAnswers] = useState({});
  const [previousFacts, setPreviousFacts] = useState({});
  const [local, setLocal] = useState(null);
  // 조사 문맥은 사실 제안·사례 검색에만 쓰고 확인된 사실만 재계산에 전달한다.
  const [researchSummary, setResearchSummary] = useState("");
  const [descriptionProposal, setDescriptionProposal] = useState(null);
  const dismissedDescriptionRef = useRef(null);
  // 웹검증이 특정한 한글 제품명으로 국내 결정례를 한 번 더 찾는다(영문 검색어는 결정례가 0건이기 쉽다).
  const [hintQueries, setHintQueries] = useState([]);
  useEffect(() => { if (llmName) setHintQueries((prev) => (prev.includes(llmName) ? prev : [...prev, llmName])); }, [llmName]);   // LLM 품명으로 결정례도 한 번 더
  const hintRecomputeRef = useRef(null);
  const seqRef = useRef(0);
  const rescueRef = useRef(null);
  const factSourceRef = useRef({});
  // 후보 호 힌트 — 제안 신호(관세율표 명칭·결정례·사무소·LLM)의 호. 트리가 없을 때 서버가 이 호로 트리를 연다(사실 아님).
  const candidateHs4s = candidateHs4Hints({ proposal, tariffItems, tariffMatches, precedentHints: preview?.row?.precedent_hints || null });
  const candidateKey = candidateHs4s.map((h) => `${h.hs4}:${h.source}`).join(",");
  // 신호 호(2026-09-25) — LLM 후보와 관세사 이력이 같은 호를 가리키면 트리가 그 호로 가정 계산한다(조건부). 사용자가 다른 후보를 고르면 그 호로.
  const brokerHistoryTop = useHistorySignal(q);
  const [signalOverride, setSignalOverride] = useState(null);
  const autoSignal = signalHeadingHint({ proposal, brokerHistory: brokerHistoryTop });
  const signalHeading = signalOverride && signalOverride.q === q
    ? { hs4: signalOverride.hs4, hs6: signalOverride.hs6 || signalSubheadingFor(signalOverride.hs4, { proposal, brokerHistory: brokerHistoryTop }), basis: signalOverride.basis || "user_alternative", alternatives: signalOverride.alternatives }
    : autoSignal;
  const signalKey = signalHeading ? `${signalHeading.basis}:${signalHeading.hs4}:${signalHeading.hs6}` : "";
  // 요청에 싣는 신호 — 화면에 이미 이 호의 가정 결과가 떠 있으면 continued(그 안에서 답을 잇는다). 서버는 사용자가 답한 뒤에는 이때만 신호로 연다(Codex 재대조 3).
  const shownRow = (local && local.q === q && local.row) || preview?.row;
  // 다시 계산하는 동안(로딩 중 빈 결과)에는 마지막으로 보인 가정을 이어 쓴다 — 첫 응답 전 두 번째 답에서 continued 가 빠지지 않게(Codex 재대조 5).
  // 가정 없는 결과가 실제로 도착했을 때(로딩이 아닐 때)만 지운다.
  const lastAssumptionRef = useRef(null);
  const recomputingNow = Boolean(local && local.q === q && local.loading);
  const rowAssumption = shownRow?.decision_tree_shadow?.signal_assumption;
  if (rowAssumption) lastAssumptionRef.current = { q, assumption: rowAssumption };
  else if (!recomputingNow || lastAssumptionRef.current?.q !== q) lastAssumptionRef.current = null;
  const shownAssumption = rowAssumption || (lastAssumptionRef.current?.q === q ? lastAssumptionRef.current.assumption : null);
  const requestSignal = signalHeading
    ? (shownAssumption && shownAssumption.hs4 === signalHeading.hs4 ? { ...signalHeading, continued: true } : signalHeading)
    : null;
  const requestSignalRef = useRef(null);
  requestSignalRef.current = requestSignal;   // 이벤트 핸들러·비동기 요청이 렌더 당시의 옛 신호가 아니라 최신 신호를 싣게(Codex 재대조 4)
  const [signalRetry, setSignalRetry] = useState(0);   // 낡은 신호 응답을 버렸을 때 다시 계산을 예약한다
  const signalRouteRef = useRef(null);
  const signalSeqRef = useRef(0);   // 신호 재계산 전용 순번 — 다른 재계산(답변·후보 호)의 요청은 건드리지 않는다
  const [signalNote, setSignalNote] = useState(null);   // 고른 다른 후보를 이 엔진이 가정 계산하지 못했을 때의 안내
  const candidateRouteRef = useRef(null);

  useEffect(() => {
    const changed = async ({ detail }) => {
      if (detail?.key !== treeFactKey(q) || detail.source === factSourceRef.current) return;
      const next = detail.facts || {};
      setAnswers(next);
      const seq = ++seqRef.current;
      // A kept-mounted tab must retract its old candidate immediately, including on failure.
      setLocal({ q, loading: true, row: {}, error: null, phase: "recompute" });
      try {
        const result = await fetchSubdivisionPreview({
          query: q, productDetails, confirmedFacts: next, researchSummary, hintQueries, candidateHs4s, signalHeading: requestSignalRef.current, legacyRow: preview?.row,
          token: localStorage.getItem("token"), fetchImpl,
        });
        if (seq !== seqRef.current) return;
        setLocal({ q, loading: false, row: result.row || {}, error: result.error });
      } catch (error) {
        if (seq === seqRef.current) setLocal({ q, loading: false, row: {}, error: String(error?.message || error), phase: "error" });
      }
    };
    window.addEventListener(TREE_FACTS_UPDATED_EVENT, changed);
    return () => window.removeEventListener(TREE_FACTS_UPDATED_EVENT, changed);
  }, [q, productDetails, researchSummary, hintQueries, fetchImpl]);

  useEffect(() => {
    // 같은 검색어로 이미 답한 사실(웹 확장 재검색·PROVE 확인 포함)은 이어서 보여 준다.
    setAnswers(loadTreeFacts(q));
    setPreviousFacts(loadUnverifiedTreeFacts(q));
    setLocal(null);
    setResearchSummary("");
    setDescriptionProposal(null);
    setHintQueries([]);
    seqRef.current += 1;
  }, [q]);

  // 조사 결과가 오면(카드 자동 투영·AI 웹검증 블록·구제) 한글 제품명으로 결정례 힌트를 다시 계산한다. 검색어당 1회.
  const applyResearchHints = async (research, baseRow) => {
    const queries = hintQueriesFromResearch(research);
    if (queries.length === 0 || hintRecomputeRef.current === q) return;
    hintRecomputeRef.current = q;
    setHintQueries(queries);
    const seq = ++seqRef.current;
    const row = baseRow || (local && local.q === q && local.row) || preview?.row;
    setLocal({ q, loading: true, row, error: null, phase: "recompute" });
    try {
      const token = localStorage.getItem("token");
      const result = await fetchSubdivisionPreview({
        query: q, productDetails, confirmedFacts: loadTreeFacts(q), researchSummary, hintQueries: queries, candidateHs4s, signalHeading: requestSignalRef.current, legacyRow: preview?.row, token, fetchImpl,
      });
      if (seq !== seqRef.current) return;
      setLocal({ q, loading: false, row: result.row || row, error: result.error });
    } catch (error) {
      if (seq !== seqRef.current) return;
      setLocal({ q, loading: false, row, error: String(error?.message || error) });
    }
  };

  // 류 후보 없음 → 웹검증으로 제품을 조사하고 그 설명으로 사전 점검을 다시 계산한다(검색어당 1회 자동).
  const rescueWithResearch = async (baseRow) => {
    rescueRef.current = q;
    const seq = ++seqRef.current;
    setLocal({ q, loading: true, row: baseRow, error: null, phase: "research" });
    try {
      const research = await loadProductResearch(q);
      if (seq !== seqRef.current) return;
      const text = researchSummaryText(research);
      if (!text) {
        setLocal({ q, loading: false, row: baseRow, error: null, phase: "no_research" });
        return;
      }
      setResearchSummary(text);
      const queries = hintQueriesFromResearch(research);
      if (queries.length) { setHintQueries(queries); hintRecomputeRef.current = q; }
      const proposed = researchDescriptionProposal(text);
      const dismissed = dismissedDescriptionRef.current?.q === q && dismissedDescriptionRef.current?.value === proposed?.value;
      setDescriptionProposal(proposed && !dismissed ? { ...proposed, q } : null);
      setLocal({ q, loading: false, row: baseRow, error: null, phase: proposed && !dismissed ? "confirm_description" : "description_required" });
    } catch (error) {
      if (seq !== seqRef.current) return;
      try { console.warn("[loginhs] pre-check rescue failed", error); } catch (_) { /* ignore */ }
      setLocal({ q, loading: false, row: baseRow, error: String(error?.message || error), phase: "error" });
    }
  };

  useEffect(() => {
    if (!q || !preview || preview.q !== q || preview.loading) return;
    const baseRow = preview.row;
    if (!baseRow || baseRow.status === "error") return;
    if (baseRow.decision_tree_shadow || baseRow.subdivision_shadow) return;
    if (rescueRef.current === q || !researchAutoEnabled()) return;
    const gate = readGateState(q);
    if (gate && gate.phase === "pass") return;
    rescueWithResearch(baseRow);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, preview]);

  // 후보 호로 트리 깨우기 — 서버 응답에 결정트리가 없고 제안 신호의 후보 호가 있으면, 검색어당 한 번 그 호를 힌트로 다시 계산한다.
  // 옛 결정표(subdivision_shadow)만 있는 경우도 포함한다(마우스 사례). 힌트는 사실이 아니며 서버가 트리를 여는 데만 쓴다.
  useEffect(() => {
    if (!q || !preview || preview.q !== q || preview.loading) return;
    const baseRow = (local && local.q === q && local.row) || preview.row;
    if (!baseRow || baseRow.status === "error") return;
    // 2026-09-27 후보 우선: 앞단 후보가 생기면 트리가 이미 깨어 있어도 한 번 더 보낸다 — 서버(라우터)가 후보 트리로 열지, 글자 깨우기를 지킬지
    // (사용자 답 진행 중·트리가 이미 정함·상품 계약) 정한다. 이미 후보로 열린 결과면 다시 보내지 않는다.
    if (baseRow.decision_tree_shadow?.routed_by_candidate) return;
    // 신호 호 재계산(LLM·이력 일치)은 이미 후보를 함께 싣는다 — 그 요청과 겹쳐 보내지 않는다.
    if (requestSignalRef.current) return;
    // 사용자가 이미 답했으면 서버도 후보 트리로 바꾸지 않는다(답한 트리를 지킨다) — 보내지 않는다.
    if (Object.keys(loadTreeFacts(q) || {}).length > 0) return;
    if (local && local.q === q && local.loading) return;
    if (!candidateHs4s.length || candidateRouteRef.current === `${q}|${candidateKey}`) return;
    candidateRouteRef.current = `${q}|${candidateKey}`;
    const seq = ++seqRef.current;
    onBackgroundStatus({ q, state: "running", text: `앞단 후보 호(${candidateHs4s.map((h) => h.hs4).join("·")})가 도착해 그 호의 결정트리로 다시 계산하고 있습니다` });
    // 진행 카드로 바꾸지 않는다 — 지금 카드(옛 결정표·제안 지도)를 그대로 두고 배경에서 한 번 시도한다.
    (async () => {
      try {
        const result = await fetchSubdivisionPreview({
          query: q, productDetails, confirmedFacts: loadTreeFacts(q), researchSummary, hintQueries, candidateHs4s, signalHeading: requestSignalRef.current, legacyRow: preview?.row,
          token: localStorage.getItem("token"), fetchImpl,
        });
        if (seq !== seqRef.current) { onBackgroundStatus(null); return; }
        const routed = result.row?.decision_tree_shadow;
        if (routed && (!baseRow.decision_tree_shadow || routed.routed_by_candidate)) {
          setLocal({ q, loading: false, row: result.row, error: result.error, phase: "candidate_routed" });
          onBackgroundStatus({ q, state: "done", text: `앞단 후보로 다시 계산해 제${routed.candidate_route?.hs4 || "?"}호 결정트리 결과로 바꿨습니다 · 처음 결과는 검색어 글자만으로 계산한 것이었습니다` });
        } else {
          onBackgroundStatus({ q, state: "done", text: "앞단 후보로 다시 계산했지만 결과는 그대로입니다" });
        }
      } catch (error) {
        if (seq !== seqRef.current) { onBackgroundStatus(null); return; }
        onBackgroundStatus({ q, state: "done", text: "앞단 후보로 다시 계산하지 못해 처음 결과를 그대로 둡니다" });
        try { console.warn("[loginhs] candidate-route recompute failed", error); } catch (_) { /* ignore */ }
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, preview, local, candidateKey]);

  // 신호 호로 다시 계산 — 검색어·신호마다 한 번. 서버가 실제로 가정 계산했을 때(signal_assumption)만 카드를 바꾼다.
  // 사용자가 류 질문에 답해 진행 중인 트리는 서버가 지킨다(라우터 answeredTreeQuestion).
  useEffect(() => {
    if (!q || !preview || preview.q !== q || preview.loading) return;
    if (local && local.q === q && local.loading) return;
    const baseRow = (local && local.q === q && local.row) || preview.row;
    if (!baseRow || baseRow.status === "error") return;
    const current = baseRow.decision_tree_shadow?.signal_assumption;
    // 지금 결과의 가정이 지금 신호와 같으면(호·소호·근거) 그대로 둔다. 신호가 바뀌었거나(소호 불일치로 빠짐 등) 사라졌으면 다시 계산한다(Codex 재대조).
    const same = current && signalHeading && current.hs4 === signalHeading.hs4 && String(current.hs6 || "") === String(signalHeading.hs6 || "") && current.basis === signalHeading.basis;
    // 이미 맞는 결과가 보이면, 그 사이 떠난 요청(예: 이력이 늦게 와 신호가 잠시 비었을 때의 '신호 없이' 요청)의 응답은 버린다(Codex 재대조 2).
    if (same || (!current && !signalHeading)) { signalSeqRef.current += 1; if (same) signalRouteRef.current = `${q}|${signalKey || "none"}`; return; }
    if (!signalHeading && current && userChosenBasis(current.basis)) return;   // 사용자가 고른 후보·소호는 신호가 사라져도 유지
    if (signalRouteRef.current === `${q}|${signalKey || "none"}`) return;
    signalRouteRef.current = `${q}|${signalKey || "none"}`;
    const seq = ++signalSeqRef.current;
    // 무효가 된 가정 결과는 다시 계산이 실패해도 남기지 않는다 — 옛 결과로 돌아가지 않고 '다시 계산 필요'로 둔다(답은 저장소에 그대로, Codex 재대조 3).
    const withdraw = () => {
      if (current && !userChosenBasis(current.basis)) setLocal({ q, loading: false, row: {}, error: "추천 호가 바뀌어 다시 계산하지 못했습니다 · 답을 하나 고치거나 다시 검색하면 다시 계산합니다", phase: "error" });
    };
    const factsAtStart = JSON.stringify(loadTreeFacts(q) || {});
    const genAtStart = seqRef.current;
    const stale = () => seqRef.current !== genAtStart || JSON.stringify(loadTreeFacts(q) || {}) !== factsAtStart;
    const retry = () => { signalRouteRef.current = null; setSignalRetry((n) => n + 1); };
    if (signalHeading) onBackgroundStatus({ q, kind: "signal", state: "running", text: signalHeading.basis === "prove_subheading"
      ? `고른 소호 ${formatHs6(signalHeading.hs6)}로 가정해 다시 계산하고 있습니다`
      : `LLM 후보와 관세사 이력이 모두 제${signalHeading.hs4}호를 가리켜 그 호로 가정해 다시 계산하고 있습니다` });
    (async () => {
      try {
        const result = await fetchSubdivisionPreview({
          query: q, productDetails, confirmedFacts: loadTreeFacts(q), researchSummary, hintQueries, candidateHs4s, signalHeading: requestSignalRef.current, legacyRow: preview?.row,
          token: localStorage.getItem("token"), fetchImpl,
        });
        if (seq !== signalSeqRef.current) return;
        // 요청이 떠난 뒤 사용자가 답했거나(사실이 바뀜) 다른 재계산이 시작됐으면 이 응답은 낡았다 — 버리고 다시 계산을 예약한다(Codex 재대조 3·4).
        if (stale()) { retry(); return; }
        if (!result.row || result.row.status === "error") { withdraw(); return; }
        // 사용자가 고른 다른 후보를 이 엔진이 계산하지 못하면(제84·85·90류 등) 카드는 두고 안내만.
        if (userChosenBasis(signalHeading?.basis) && !result.row.decision_tree_shadow?.signal_assumption) { setSignalNote({ q, hs4: signalHeading.hs4 }); return; }
        // 자동 신호는 서버가 가정하지 않은 결과(사용자 답을 지킨 트리 등)도 그대로 받는다 — 옛 가정을 남기지 않는다(Codex 재대조 2).
        setSignalNote(null);
        setLocal({ q, loading: false, row: result.row, error: result.error, phase: "signal_heading" });
        onBackgroundStatus(result.row.decision_tree_shadow?.signal_assumption
          ? { q, state: "done", text: result.row.decision_tree_shadow.signal_assumption.basis === "prove_subheading"
            ? `고른 소호 ${formatHs6(result.row.decision_tree_shadow.signal_assumption.hs6)}로 가정해 다시 계산한 결과입니다 · 조건부 · 확정 아님`
            : `LLM 후보와 관세사 이력이 가리킨 제${result.row.decision_tree_shadow.signal_assumption.hs4}호로 가정해 다시 계산한 결과입니다 · 조건부 · 확정 아님` }
          : { q, state: "done", text: "추천 호로 다시 계산했지만 가정하지 않은 결과를 그대로 보입니다" });
      } catch (error) {
        if (seq !== signalSeqRef.current) return;
        // 낡은 요청의 실패가 그 사이 나온 최신 결과를 지우지 않게 — 실패도 같은 검사를 거친다(Codex 재대조 4).
        if (stale()) { retry(); return; }
        try { console.warn("[loginhs] signal-heading recompute failed", error); } catch (_) { /* ignore */ }
        withdraw();
      }
    })().finally(() => onBackgroundStatus((prev) => (prev && prev.kind === "signal" && prev.state === "running" ? null : prev)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, preview, local, signalKey, signalRetry]);

  if (!q || !preview || preview.q !== q) return null;

  const localActive = local && local.q === q;
  const recomputing = Boolean(localActive && local.loading);

  if (preview.loading && !localActive) {
    return (
      <ProgressCard
        title="결정트리 사전 점검을 계산하고 있습니다"
        detail="검색어의 문언으로 류·호·소호 후보와 확인해야 할 사실을 계산합니다. 보통 몇 초 걸립니다."
        steps={[
          { label: "관세율표 검색", state: "done" },
          { label: "결정트리 사전 점검", state: "active" },
          { label: "웹검증 결과 맞추기", state: "pending" },
          { label: "사실 확인 · 세번 계산", state: "pending" },
        ]}
      />
    );
  }

  const row = (localActive && local.row) || preview.row;
  if (!row || row.status === "error") return null;
  const shadow = row.subdivision_shadow;
  const tree = row.decision_tree_shadow && typeof row.decision_tree_shadow === "object"
    ? row.decision_tree_shadow
    : null;

  // 여러 사실(웹검증 제안 일괄 확인)도 한 번의 재계산으로 반영한다.
  const answerMany = async (entries) => {
    if (recomputing) return;
    const merged = { ...answers };
    Object.entries(entries || {}).forEach(([key, value]) => {
      // 값이 null이면 그 답을 지운다(저장소가 빈 값을 버린다).
      if (value === null || value === undefined) delete merged[key];
      else merged[key] = value;
      // 방호·차폐복임을 새로 확인하면 이전에 고른 의류 종류(스커트 등)는 버린다. 그래야 트리가
      // "그 밖의 의류" 경로를 다시 계산한다. 필요하면 사용자가 의류 종류를 다시 고를 수 있다.
    });
    // 방호·차폐복 확인이 들어오면 의류 종류 답(웹검증 제안 포함)은 버린다. 방호복은 트리가 "그 밖의 의류"
    // 경로(제6211호)로 계산하며, 사용자 확인 의류 종류가 남으면 스커트·조끼 호가 그 규칙을 덮어쓴다.
    if (String(entries?.protective_apparel_status ?? "") === "true") delete merged.garment_type;
    const next = saveTreeFacts(q, merged, factSourceRef.current);
    setAnswers(next);
    const seq = ++seqRef.current;
    setLocal({ q, loading: true, row, error: null, phase: "recompute" });
    try {
      const token = localStorage.getItem("token");
      const result = await fetchSubdivisionPreview({
        query: q, productDetails, confirmedFacts: next, researchSummary, hintQueries, candidateHs4s, signalHeading: requestSignalRef.current, legacyRow: preview?.row, token, fetchImpl,
      });
      if (seq !== seqRef.current) return;
      setLocal({ q, loading: false, row: result.row || row, error: result.error,
        phase: result.row?.decision_tree_shadow ? "rescued" : "still_no_tree" });
      if (!result.error && result.row) setDescriptionProposal(null);
    } catch (error) {
      if (seq !== seqRef.current) return;
      setLocal({ q, loading: false, row, error: String(error?.message || error), phase: "error" });
    }
  };
  const answer = (key, value) => answerMany({ [key]: value });

  if (tree) {
    return (
      <DecisionTreeCard
        tree={tree}
        queryTerms={queryTerms}
        precedentHints={row.precedent_hints && typeof row.precedent_hints === "object" ? row.precedent_hints : null}
        shadow={shadow}
        answers={answers}
        previousFacts={previousFacts}
        recomputing={recomputing}
        error={localActive ? local.error : null}
        onAnswer={answer}
        onAnswerMany={answerMany}
        onRemoveAnswer={(key) => answer(key, null)}
        onOpenProve={onOpenProve}
        onResearchReady={(research) => applyResearchHints(research, row)}
        onChooseSignalHeading={(hs4) => setSignalOverride({ q, hs4, alternatives: [tree?.signal_assumption?.hs4, ...(tree?.signal_assumption?.alternatives || [])].filter((h) => h && h !== hs4) })}
        onChooseProveSubheading={(p) => setSignalOverride({ q, hs4: p.hs4, hs6: p.hs6, basis: "prove_subheading", alternatives: [] })}
        signalNote={signalNote && signalNote.q === q ? signalNote : null}
        productDetails={productDetails}
        fetchImpl={fetchImpl}
        q={q}
        tariffMatches={tariffMatches}
        headingMatches={trustedHeadingMatches}
        tariffItemsAll={tariffItems}
        proposal={proposal}
      />
    );
  }

  // 결정트리가 검색어만으로 류를 고르지 못한 경우. 카드를 숨기면 프로세스의 주인이 사라지므로
  // 여기서 웹검증 제품 조사 → 제품 설명으로 재계산까지 이어 주고, 안 되면 다음 행동을 안내한다.
  if (!shadow) {
    const phase = localActive ? local.phase : null;
    const chapter90 = String(row.chapter || "") === "90";
    if (recomputing) {
      return (
        <ProgressCard
          title={phase === "research" ? "AI 웹검증으로 제품을 조사하고 있습니다" : "제품 설명으로 결정트리를 다시 계산하고 있습니다"}
          detail={phase === "research"
            ? "검색어만으로는 류를 고르지 못했습니다. 제조사·공식 자료에서 제품 설명을 찾아 다시 계산합니다. 첫 조회는 20초 이상 걸릴 수 있습니다."
            : "직접 확인한 상품 설명으로 류·호 후보를 다시 계산합니다."}
          steps={[
            { label: "결정트리 사전 점검", state: "done" },
            { label: "AI 웹검증 제품 조사", state: phase === "research" ? "active" : "done" },
            { label: "제품 설명으로 다시 계산", state: phase === "recompute" ? "active" : "pending" },
            { label: "사실 확인 · 세번 계산", state: "pending" },
          ]}
        />
      );
    }
    const message = phase === "no_research"
      ? "웹조사에서 제품 설명을 찾지 못했습니다. 알고 있는 재질·구성·사용 방법을 직접 적어 주세요."
      : phase === "still_no_tree"
        ? "확인한 상품 설명으로도 분류 경로를 정하지 못했습니다. 설명을 보완해 다시 시도할 수 있습니다."
        : phase === "confirm_description" ? "웹조사에서 찾은 설명이 실제 수입 물품과 같은지 확인해 주세요. 확인한 설명만 계산에 사용합니다."
        : phase === "description_required" ? "조사 내용이 길어 자동으로 답을 채우지 않았습니다. 재질·구성·사용 방법을 직접 정리해 주세요."
        : phase === "error"
          ? "제품 조사 또는 상품 설명 반영 중 오류가 났습니다. 확인한 답은 보존했습니다. 다시 시도해 주세요."
          : "검색어만으로는 결정트리가 류를 고르지 못했습니다.";
    return (
      <div
        data-testid="subdivision-preview-unavailable"
        style={{ ...cardStyle, borderLeft: "4px solid #94a3b8", padding: "11px 14px" }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <strong style={{ fontSize: 13 }}>PROVE 결정트리 사전 점검</strong>
          <span style={draftBadgeStyle}>류 후보 없음</span>
        </div>
        {tariffMatches.length > 0 && (
          <TariffNameBanner
            classified={classifyTariffMatches(tariffMatches, [])}
            tree={null}
            precedentHints={row.precedent_hints && typeof row.precedent_hints === "object" ? row.precedent_hints : null}
            fetchImpl={fetchImpl}
            onOpenProve={onOpenProve}
          />
        )}
        <div style={{ marginTop: 6, fontSize: 12, lineHeight: 1.55 }}>{message}</div>
        <ProposalOnlyMapBlock q={q} queryTerms={queryTerms} proposal={proposal} tariffItems={tariffItems} tariffMatches={tariffMatches} headingMatches={trustedHeadingMatches} precedentHints={row.precedent_hints && typeof row.precedent_hints === "object" ? row.precedent_hints : null} fetchImpl={fetchImpl} productDetails={productDetails} onAnswer={answer} disabled={recomputing} />
        {descriptionProposal?.q === q && <section aria-label="웹조사 설명 확인">
          <p>웹조사 요약 · 원문 사실로 확정되지 않은 제안입니다.</p>
          <p>{descriptionProposal.value}</p>
          <button type="button" onClick={() => answer('product_description', descriptionProposal.value)}>내 상품 설명과 일치합니다 · 확인 후 재계산</button>
          <button type="button" onClick={() => { dismissedDescriptionRef.current = {q, value:descriptionProposal.value}; setDescriptionProposal(null); setLocal(s => ({...s,phase:'description_required'})); }}>이 설명 제외</button>
        </section>}
        <ProductFactDialoguePanel key={q} tree={{ product_fact_dialogue: { status: answers.product_description === 'unknown' ? 'needs_documents' : 'needs_product_facts', title: '상품 설명 입력', description: '알고 있는 제품 정보를 알려주세요.', questions: answers.product_description === 'unknown' ? [] : [{ fact_key: 'product_description', prompt_ko: '물품의 재질·구성·사용 방법을 설명해 주세요.', options: [] }], document_requests: answers.product_description === 'unknown' ? [{fact_key:'product_description',label:'제품 구성·사용 방법',guidance:'설명서나 공급업체에서 정보를 확인한 뒤 다시 답해 주세요.'}] : [] } }}
          onAnswer={answer} answers={answers} disabled={recomputing} />
        {phase === "error" && local?.error && (
          <div style={{ marginTop: 4, fontSize: 11, color: "var(--audit-text-muted)" }}>오류: {String(local.error).slice(0, 160)}</div>
        )}
        {chapter90 && (
          <div style={{ marginTop: 6, fontSize: 12, color: "var(--audit-text-muted)", lineHeight: 1.55 }}>
            제90류로 분류됐지만 현재 판정에 사용하는 confirmed 결정표가 없거나 10자리 세번이 아직 확정되지 않았습니다.
            기존 검색 결과는 그대로 유지됩니다.
          </div>
        )}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginTop: 8, alignItems: "center" }}>
          {phase !== "no_research" && phase !== "still_no_tree" && (
            <button
              type="button"
              onClick={() => rescueWithResearch(row)}
              style={optionButtonStyle(false)}
              title="AI 웹검증으로 제품 설명을 찾아 결정트리를 다시 계산합니다"
            >
              {phase === "error" ? "제품 조사 다시 시도" : "웹검증으로 제품 조사"}
            </button>
          )}
          {onOpenProve && (
            <button type="button" onClick={() => onOpenProve()} style={optionButtonStyle(true)}>
              확정 검토 · 보고서
            </button>
          )}
        </div>
      </div>
    );
  }

  const meta = statusMeta(shadow);
  const supported = shadow.supported_hsk
    ? [shadow.supported_hsk]
    : Array.isArray(shadow.supported_hsks)
      ? shadow.supported_hsks
      : [];
  const questions = Array.isArray(shadow.questions)
    ? shadow.questions.filter(Boolean)
    : [];

  return (
    <div
      data-testid="subdivision-preview-card"
      style={{ ...cardStyle, border: "1px solid var(--audit-border)", borderLeft: `4px solid ${meta.border}` }}
    >
      <CardHeader title="국내세분 결정표 사전 점검" meta={meta} />
      {/* 결정트리가 이 물품을 못 알아본 경우(예: 마우스 — 제84류 트리 키워드 없음). 옛 결정표(기존 세번 기준)만 보이던 자리에
          제안 신호(LLM 후보·결정례·관세율표 명칭 일치)로 지도를 먼저 그린다 — 2026-09-14 사용자: "이건 왜 지도·표가 안 나오지?" */}
      <div data-testid="legacy-map-note" style={{ marginTop: 8, fontSize: 11, color: "var(--audit-text-muted)", lineHeight: 1.5, display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
        <span>결정트리가 이 물품을 인식하지 못했습니다 · 아래 지도는 제안 신호(LLM 후보·결정례·명칭 일치)로 그렸고, 결정표 질문은 기존 세번 기준입니다 · 확정 아님</span>
        <button type="button" onClick={() => rescueWithResearch(row)} style={optionButtonStyle(false)} title="AI 웹검증으로 제품 설명을 찾아 결정트리를 다시 계산합니다">웹검증으로 제품 조사</button>
      </div>
      <ProposalOnlyMapBlock q={q} queryTerms={queryTerms} proposal={proposal} tariffItems={tariffItems} tariffMatches={tariffMatches} headingMatches={trustedHeadingMatches} precedentHints={row.precedent_hints && typeof row.precedent_hints === "object" ? row.precedent_hints : null} fetchImpl={fetchImpl} productDetails={productDetails} onAnswer={answer} disabled={recomputing} />

      <div style={gridStyle}>
        <ResultCell
          label="PROVE 결정표 경로"
          value={supported.length ? supported.map(formatSubdivisionHsk).join(", ") : "판별 사실 필요"}
        />
      </div>

      {questions.length > 0 && (
        <div style={{ marginTop: 9, fontSize: 12, lineHeight: 1.55 }}>
          <b>추가 확인</b>
          <ul style={{ margin: "4px 0 0", paddingLeft: 19 }}>
            {questions.map((question, index) => <li key={`${question}-${index}`}>{question}</li>)}
          </ul>
        </div>
      )}
      {Array.isArray(shadow.deferred_questions) && shadow.deferred_questions.length > 0 && (
        <div style={{ marginTop: 7, fontSize: 11, color: "var(--audit-text-muted)" }}>
          위 사실을 확인하면 이어지는 질문 {shadow.deferred_questions.length}개가 있습니다.
        </div>
      )}

      <CardFooter onOpenProve={onOpenProve} />
    </div>
  );
}

function outOfScopeExplanation(reason) {
  const text = String(reason || "");
  if (text === "product_scope_not_found") return "아직 상품의 종류를 구별하지 못했습니다. 제품 설명을 보완하거나 웹조사를 진행해 주세요.";
  const exclusion = text.match(/^chapter(\d+)_exclusion_status_yes$/);
  if (exclusion) {
    return `제${exclusion[1]}류 주(注)에 따른 제외 물품이라고 답하셨기 때문에 이 트리 범위 밖으로 닫혔습니다. 잘못 답했다면 아래 '내가 답한 사실'에서 지우세요.`;
  }
  return `이 품목은 현재 결정트리의 법정 범위 밖입니다${text ? ` (${humanizeCode(text)})` : ""}.`;
}

/** 범용 류 트리는 facts를 {키: 값} 객체로 내려준다. 카드는 배열 형태로 통일해 그린다. */
function normalizeTreeFacts(rawFacts, answers) {
  if (Array.isArray(rawFacts)) return rawFacts;
  if (!rawFacts || typeof rawFacts !== "object") return [];
  return Object.entries(rawFacts).map(([key, value]) => ({
    key,
    value: value && typeof value === "object" && !['flower_component_manifest','noodle_component_manifest'].includes(key) ? JSON.stringify(value) : value,
    status: Object.prototype.hasOwnProperty.call(answers || {}, key) ? "user_confirmed" : "explicit",
  }));
}

/**
 * 신호 호 가정의 류 주 확인(2026-09-25, 검수 v2·v3) — 신호 호로 계산하면서 '이 류의 주가 빼는 물품이 아니다'를 가정했음을 밝히고,
 * 류 주·부 주의 제외 조항 **원문 목록을 바로 펼쳐** 보인 뒤에만 답하게 한다(obs_note_listed_chNN).
 * 목록이 잘렸으면 '일부만 보임', 조항을 찾지 못했거나 불러오지 못했으면 답을 막고 주·해설 탭을 안내한다(가정은 그대로 조건부로 남는다).
 * 답하면 가정이 풀리거나(목록에 없음) 트리가 이 류 밖으로 닫는다(목록에 있음). 답은 지울 수 있다.
 */
function SignalNoteConfirm({ tree, answers = {}, onAnswer, fetchImpl, disabled = false }) {
  const assumption = tree?.signal_assumption;
  const hs4 = String(assumption?.hs4 || "");
  const chapter = hs4.slice(0, 2);
  const key = `obs_note_listed_ch${chapter}`;
  const answer = answers[key];
  const assumedExclusion = (Array.isArray(assumption?.assumed_keys) ? assumption.assumed_keys : []).some((k) => /_exclusion_status$/.test(String(k)));
  const needList = Boolean(assumption && onAnswer && assumedExclusion && answer !== "not_listed" && answer !== "listed");
  const [state, setState] = useState({ chapter: null, loading: false, notes: null, error: false });
  useEffect(() => {
    if (!needList || state.chapter === chapter) return undefined;
    let alive = true;
    setState({ chapter, loading: true, notes: null, error: false });
    fetchExclusionNotes({ chapter, hs4, fetchImpl })
      .then((notes) => { if (alive) setState({ chapter, loading: false, notes, error: false }); })
      .catch(() => { if (alive) setState({ chapter, loading: false, notes: null, error: true }); });
    return () => { alive = false; };
  }, [needList, chapter]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!assumption || !onAnswer) return null;
  const btn = { marginLeft: 6, border: "1px solid var(--audit-border)", borderRadius: 999, padding: "0 9px", fontSize: 11, background: "transparent", color: "#1d4ed8", cursor: "pointer", fontFamily: "inherit" };
  const ch = Number(chapter);
  if (answer === "not_listed" || answer === "listed") {
    return (
      <div data-testid="signal-note-answered" style={{ marginTop: 4 }}>
        {answer === "not_listed" ? `✓ 제${ch}류 주가 빼는 물품 목록에 없다고 답했습니다` : `제${ch}류 주가 빼는 물품 목록에 있다고 답했습니다 — 이 류가 아닙니다`}
        <button type="button" disabled={disabled} onClick={() => onAnswer(key, null)} style={btn}>다시 답하기</button>
      </div>
    );
  }
  if (!assumedExclusion) return null;
  const notes = state.chapter === chapter ? state.notes : null;
  const clauseCount = notes ? ((notes.chapter?.clauses?.length || 0) + (notes.section?.clauses?.length || 0)) : 0;
  const truncated = Boolean(notes && (notes.chapter?.clauses?.truncated || notes.section?.clauses?.truncated));
  // 류 주·부 주 어느 한쪽이라도 불러오지 못했으면 답하지 못한다 — 일부만 보고 '목록에 없음'을 확인하게 하지 않는다(Codex 재대조).
  const ready = clauseCount > 0 && !notes?.failed;
  return (
    <div data-testid="signal-note-confirm" style={{ marginTop: 4 }}>
      <div>제{ch}류 주가 이 류에서 빼는 물품이 아니라고도 가정했습니다. 아래 원문 목록을 보고 답해 주세요.
        <button type="button" disabled={disabled || !ready} onClick={() => onAnswer(key, "not_listed")} style={btn}>목록에 없습니다</button>
        <button type="button" disabled={disabled || !ready} onClick={() => onAnswer(key, "listed")} style={btn}>목록에 있습니다</button>
      </div>
      {state.loading && <div role="status">제외 조항 원문을 불러오는 중…</div>}
      {!state.loading && (state.error || (notes && !ready)) && (
        <div data-testid="signal-note-unavailable">제외 조항 원문을 {state.error || notes?.failed ? (clauseCount > 0 ? "일부만 불러왔습니다(류 주 또는 부 주를 불러오지 못함)" : "불러오지 못했습니다") : "찾지 못했습니다"} — 주·해설 탭에서 확인하기 전에는 답할 수 없고 결과는 조건부로 남습니다.</div>
      )}
      {clauseCount > 0 && (
        <div data-testid="signal-note-list" style={{ border: "1px solid var(--audit-border)", borderRadius: 6, padding: "6px 9px", marginTop: 3, maxHeight: 260, overflowY: "auto", fontSize: 11 }}>
          {[[notes.chapter, `제${ch}류 주`], [notes.section, "부 주"]].map(([note, label]) => note && note.clauses.length > 0 && (
            <div key={label} style={{ marginTop: 4 }}>
              <b>{note.label || label}</b>
              {note.clauses.map((clause, index) => (
                <div key={`${clause.title}-${index}`} style={{ marginTop: 3 }}>
                  <div>{clause.title}</div>
                  {clause.items.length > 0 && <ul style={{ margin: "2px 0 0", paddingLeft: 18 }}>{clause.items.map((item, n) => <li key={`${item}-${n}`}>{item}</li>)}</ul>}
                </div>
              ))}
            </div>
          ))}
          <div style={{ marginTop: 4, color: "var(--audit-text-muted)" }}>
            여기 열거된 물품이면 이 류가 아닙니다.{truncated ? ` 목록 일부만 보입니다(남은 약 ${(notes.chapter?.clauses?.remainingLines || 0) + (notes.section?.clauses?.remainingLines || 0)}줄) — 전문은 주·해설 탭에서 확인한 뒤 답해 주세요.` : " 전문은 주·해설 탭에 있습니다."}
          </div>
        </div>
      )}
    </div>
  );
}

/** 선택지 라벨 앞의 코드("7318 · …", "7318.15 · …")를 자릿수 문자열로 뽑는다. 없으면 "". */
function optionCodePrefix(label) {
  const m = String(label || "").match(/^(\d{4})(?:\.(\d{2}))?(?:-(\d{4}))?\s*·/);
  if (!m) return "";
  return `${m[1]}${m[2] || ""}${m[3] || ""}`;
}

/** 결정례가 그 선택지(코드 접두)로 가는 건수. */
function precedentCountFor(hints, codePrefix) {
  if (!hints || !codePrefix || !Array.isArray(hints.by_hsk)) return 0;
  return hints.by_hsk.reduce((sum, row) => (String(row.hsk || "").startsWith(codePrefix) ? sum + Number(row.count || 0) : sum), 0);
}

/** 트리가 보고 있는 범위(확정 호 → 후보 호 → 류)의 코드 접두. 결정례 힌트를 이 범위 안팎으로 나눈다. */
function treeScopePrefixes(tree) {
  if (tree?.supported_hs4) return [String(tree.supported_hs4)];
  if (Array.isArray(tree?.hs4_candidates) && tree.hs4_candidates.length > 0) return tree.hs4_candidates.map(String);
  const keys = [
    ...(Array.isArray(tree?.questions) ? tree.questions.map((q) => q && q.fact_key) : []),
    ...(tree?.facts && !Array.isArray(tree.facts) ? Object.keys(tree.facts) : []),
    tree?.reason,
  ];
  for (const key of keys) {
    const m = String(key || "").match(/^chapter(\d{2})_/);
    if (m) return [m[1]];
  }
  return [];
}

function PrecedentHintRow({ row, onSelect, selected, recomputing }) {
  return (
    <li data-testid="precedent-hint-row">
      <b style={{ fontFamily: "JetBrains Mono, monospace" }}>{formatSubdivisionHsk(row.hsk)}</b>
      {" · "}{row.count}건
      {row.label ? <> · {row.label}</> : null}
      {row.latest_date ? <span style={{ color: "var(--audit-text-muted)" }}> ({row.latest_date})</span> : null}
      {onSelect && (
        <button
          type="button"
          disabled={recomputing}
          aria-pressed={selected}
          onClick={() => onSelect(row.hsk)}
          style={{ ...optionButtonStyle(selected), marginLeft: 8, padding: "1px 8px", fontSize: 11 }}
        >
          이 후보의 질문 보기
        </button>
      )}
      {row.desc ? <div style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>{row.desc}</div> : null}
    </li>
  );
}

/**
 * 결정례 합의 배너 — 국내 결정례가 한 세번으로 모이면 그 세번을 카드 맨 위에 먼저 보여 준다.
 * 상태는 결정트리와의 관계로 정한다: 같은 세번에 닿음(파랑) → 물품 사실 질문에서 멈춤(회색,
 * 세번은 그대로 크게) → 트리 결과가 다름(회색 안내). 확정은 결정트리(PROVE)가 한다.
 */
function PrecedentConsensusBanner({ consensus, relation, remaining, tariffAgrees, onOpenProve }) {
  const code = formatSubdivisionHsk(consensus.hsk);
  const share = Math.round(consensus.share * 100);
  const basis = (consensus.title
    ? `제목이 검색어와 같은 국내 결정례 ${consensus.count}건 · 반대 사례 없음` + (consensus.corrected ? ` · 검색어 교정 "${consensus.corrected}"` : "")
    : `검색된 국내 사례 ${consensus.total}건 중 ${consensus.count}건(${share}%)`
      + (consensus.outside_count > 0 ? ` · 다른 호 사례 ${consensus.outside_count}건` : "")
      + " · 검색 표본 내 비율")
    + (tariffAgrees ? " · 관세율표 품명도 일치" : "");
  let title;
  let detail;
  let palette = GREY;
  if (relation === "matched") {
    title = "결정트리 계산과 사례 후보가 같습니다";
    detail = `${basis}과 결정트리 계산이 같은 세번입니다.`;
    palette = BLUE;
  } else if (relation === "differs") {
    title = "결정례와 결정트리가 다릅니다";
    detail = `${basis}은 이 세번이지만 결정트리는 다른 경로를 계산했습니다. 상품 사실을 확인해 주세요.`;
  } else {
    title = "결정례 기준 세번";
    detail = remaining > 0
      ? `${basis}. 결정트리가 물품 사실 확인 ${remaining}개를 남겼습니다. 답변을 확인한 뒤 PROVE가 다시 판정합니다.`
      : `${basis}. 결정트리 확정 전 참고 세번입니다.`;
  }
  return (
    <div
      data-testid="precedent-consensus-banner"
      data-relation={relation}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        marginBottom: 8,
        padding: "9px 12px",
        borderRadius: 6,
        border: `1px solid ${palette.border}`,
        background: palette.background,
      }}
    >
      <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 20, fontWeight: 700, color: palette.color, letterSpacing: 0.2 }}>
        {code}
      </div>
      <div style={{ flex: 1, minWidth: 200, fontSize: 12, lineHeight: 1.5 }}>
        <b style={{ color: palette.color }}>{title}</b>
        {consensus.label ? <span style={{ marginLeft: 6, color: "var(--audit-text-muted)" }}>{consensus.label}</span> : null}
        {consensus.latest_date ? <span style={{ marginLeft: 6, color: "var(--audit-text-muted)" }}>최신 {consensus.latest_date}</span> : null}
        <div style={{ color: "var(--audit-text-muted)" }}>{detail}</div>
      </div>
      {onOpenProve && relation !== "differs" && (
        <button type="button" onClick={() => onOpenProve()} style={openButtonStyle}>
          확정 검토 · 보고서
        </button>
      )}
    </div>
  );
}

/** 결정례 힌트에서 특정 세번의 국내 결정례 건수. */
function precedentCountForHsk(hints, hsk) {
  const rows = Array.isArray(hints?.by_hsk) ? hints.by_hsk : [];
  const row = rows.find((r) => digitsOf(r?.hsk) === digitsOf(hsk));
  return row ? Number(row.count || 0) : 0;
}

/**
 * 관세율표 품명 일치 배너 — 검색어가 관세율표 10자리 명칭과 같을 때.
 * - 한 줄·트리 범위 안: 참고 후보와 남은 질문을 표시한다. 답은 사용자가 직접 확인한다.
 * - 한 줄·트리 범위 밖: 부모 호의 조건(고무제 의류 등)을 붙인 후보로만 두고 트리 질문이 가르게 둔다
 * - 복수: 같은 이름의 세번을 부모 호 뜻과 함께 나란히 보이고, 누르면 그 경로를 적용한다
 * 확정은 결정트리(PROVE)만 한다.
 */
function TariffNameBanner({ classified, tree, relation, remaining, precedentHints, fetchImpl, recomputing, onSelect, onOpenProve }) {
  const matches = classified.matches;
  const titles = useHeadingTitles(matches.map((m) => m.hs4), fetchImpl || fetch);
  const headingLine = (m) => {
    const title = shortHeadingTitle(titles[m.hs4]);
    return `제${m.hs4}호${title ? ` · ${title}` : ""}`;
  };
  const precedentNote = (m) => {
    const n = precedentCountForHsk(precedentHints, m.hsk);
    return n > 0 ? ` · 국내 결정례 ${n}건 같은 세번` : "";
  };
  const wrap = (palette, children) => (
    <div
      data-testid="tariff-name-banner"
      data-kind={classified.kind}
      data-relation={relation || "none"}
      style={{
        display: "flex",
        alignItems: "center",
        gap: 12,
        flexWrap: "wrap",
        marginBottom: 8,
        marginTop: 6,
        padding: "9px 12px",
        borderRadius: 6,
        border: `1px solid ${palette.border}`,
        background: palette.background,
      }}
    >
      {children}
    </div>
  );

  if (classified.kind === "multiple") {
    const scope = treeScopePrefixes(tree);
    return wrap(GREY, (
      <div style={{ flex: 1, minWidth: 220, fontSize: 12, lineHeight: 1.55 }}>
        <b>검색된 관세율표에 같은 이름의 후보가 {matches.length}개 있습니다</b>
        <div style={{ color: "var(--audit-text-muted)" }}>
          물품이 어느 호에 해당하는지에 따라 갈립니다. 상품 사실을 확인하면 PROVE가 관련 기준을 대조합니다.
        </div>
        <ul style={{ margin: "5px 0 0", paddingLeft: 0, listStyle: "none" }}>
          {matches.map((m) => {
            const selectable = Boolean(tree) && Boolean(onSelect) && scope.some((p) => m.hsk.startsWith(p));
            return (
              <li key={m.hsk} data-testid="tariff-name-candidate" style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginTop: 3 }}>
                <b style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 15, color: BLUE.color }}>{formatSubdivisionHsk(m.hsk)}</b>
                <span>{m.ko}{m.en ? <span style={{ color: "var(--audit-text-muted)" }}> ({m.en})</span> : null}</span>
                <span style={{ color: "var(--audit-text-muted)" }}>{headingLine(m)}{precedentNote(m)}</span>
                {selectable && (
                  <button type="button" disabled={recomputing} onClick={() => onSelect(m.hsk)} style={{ ...optionButtonStyle(false), padding: "1px 8px", fontSize: 11 }}>
                    이 후보의 질문 보기
                  </button>
                )}
              </li>
            );
          })}
        </ul>
      </div>
    ));
  }

  const m = matches[0];
  const code = formatSubdivisionHsk(m.hsk);
  const nameLine = `${m.ko}${m.en ? ` (${m.en})` : ""}`;
  if (classified.kind === "single_out_of_scope") {
    const outside = Array.isArray(tree?.outside_chapter_candidates) ? tree.outside_chapter_candidates.map(String) : [];
    const scope = treeScopePrefixes(tree);
    const detail = tree
      ? `${headingLine(m)}의 조건을 충족할 때만 이 세번입니다. 결정트리는 지금 ${scope.length ? `제${scope.join("·")}호` : "다른 류"}를 보고 있습니다.`
        + (outside.includes(m.hs4) ? " 결정트리도 이 호를 범위 밖 후보로 두었으므로 위 질문의 답에 따라 갈립니다." : "")
      : `${headingLine(m)}의 조건을 충족할 때만 이 세번입니다. 결정트리가 아직 류를 고르지 못했습니다.`;
    return wrap(GREY, (
      <>
        <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 18, fontWeight: 700, color: GREY.color }}>{code}</div>
        <div style={{ flex: 1, minWidth: 200, fontSize: 12, lineHeight: 1.5 }}>
          <b>관세율표에 같은 이름의 세번이 있습니다</b>
          <span style={{ marginLeft: 6, color: "var(--audit-text-muted)" }}>{nameLine}{precedentNote(m)}</span>
          <div style={{ color: "var(--audit-text-muted)" }}>{detail}</div>
        </div>
      </>
    ));
  }

  // single_in_scope
  let title;
  let detail;
  let palette = GREY;
  if (relation === "matched") {
    title = "결정트리 계산과 품명 후보가 같습니다";
    detail = `관세율표 10자리 명칭이 검색어와 같고 결정트리 계산도 같은 세번입니다. ${headingLine(m)}`;
    palette = BLUE;
  } else if (relation === "differs") {
    title = "관세율표 품명과 결정트리가 다릅니다";
    detail = `이름은 이 세번이지만 결정트리는 다른 경로를 계산했습니다. 상품 사실을 확인해 주세요. ${headingLine(m)}`;
  } else {
    title = m.qualified ? "관세율표 품명 후보 · 조건 확인 필요" : m.llm ? "관세율표 품명 후보 · LLM이 읽은 품명 일치" : m.modifier ? "관세율표 품명 후보 · 검색어 끝 낱말 일치" : m.synonym ? "관세율표 품명 후보 · 검색 엔진 동의어 일치" : "관세율표 품명 후보";
    detail = remaining > 0
      ? `${m.qualified ? "괄호의 한정 조건을 확인해 주세요. " : m.llm ? `LLM이 이 물품을 "${m.llmName}"으로 읽었고 그 이름이 관세율표 명칭과 같습니다(LLM은 이름만 제안 · 세번은 명칭 비교가 정함 · 확정 아님). ` : m.synonym ? "검색어의 낱말 전부가 동의어 사전으로 이 세번 이름과 맞았습니다(LLM 아님). " : ""}${headingLine(m)}. 결정트리가 물품 사실 확인 ${remaining}개를 남겼습니다. 답변을 확인한 뒤 PROVE가 다시 판정합니다.`
      : `${headingLine(m)}. 결정트리 확정 전 참고 세번입니다.`;
  }
  return wrap(palette, (
    <>
      <div style={{ fontFamily: "JetBrains Mono, monospace", fontSize: 20, fontWeight: 700, color: palette.color, letterSpacing: 0.2 }}>{code}</div>
      <div style={{ flex: 1, minWidth: 200, fontSize: 12, lineHeight: 1.5 }}>
        <b style={{ color: palette.color }}>{title}</b>
        <span style={{ marginLeft: 6, color: "var(--audit-text-muted)" }}>{nameLine}{precedentNote(m)}</span>
        <div style={{ color: "var(--audit-text-muted)" }}>{detail}</div>
      </div>
      {onOpenProve && relation !== "differs" && (
        <button type="button" onClick={() => onOpenProve()} style={openButtonStyle}>
          확정 검토 · 보고서
        </button>
      )}
    </>
  ));
}

function PrecedentHintsBlock(props) {
  return ['juice','milling','food_preparation'].includes(props.tree?.product_fact_dialogue?.product_intake?.kind)
    ? <details style={{marginTop:12}}><summary>관련 사례 비교 · 상품 답변과 별도로 확인</summary><PrecedentHintsContent {...props} /></details>
    : <PrecedentHintsContent {...props} />;
}
function PrecedentHintsContent({ hints, tree, onSelect, selectedHsk, recomputing }) {
  const [showOthers, setShowOthers] = useState(false);
  if (!hints || !Array.isArray(hints.by_hsk) || hints.by_hsk.length === 0) return null;
  const prefixes = treeScopePrefixes(tree);
  const inScope = prefixes.length
    ? hints.by_hsk.filter((row) => prefixes.some((p) => String(row.hsk || "").startsWith(p)))
    : hints.by_hsk;
  const others = hints.by_hsk.filter((row) => !inScope.includes(row));
  const otherCount = others.reduce((sum, row) => sum + Number(row.count || 0), 0);
  return (
    <div style={{ marginTop: 9, fontSize: 12, lineHeight: 1.55 }}>
      <b>국내 결정례 {hints.domestic_total}건</b>
      <span style={{ marginLeft: 6, fontSize: 11, color: "var(--audit-text-muted)" }}>
        근거로만 씁니다. 결정례가 가리키는 선택지에 표시를 붙였을 뿐 자동 확정하지 않습니다.
        {Number(hints.superseded) > 0 ? ` 폐기된 결정례 ${hints.superseded}건은 제외했습니다.` : ""}
      </span>
      {inScope.length === 0 && (
        <div style={{ marginTop: 4, color: "var(--audit-text-muted)" }}>이 트리 범위(제{prefixes.join("·")}) 안의 결정례는 없습니다.</div>
      )}
      <ul style={{ margin: "4px 0 0", paddingLeft: 19 }}>
        {inScope.map((row) => (
          <PrecedentHintRow key={row.hsk} row={row} onSelect={onSelect} selected={selectedHsk === row.hsk} recomputing={recomputing} />
        ))}
      </ul>
      {inScope.length > 0 && onSelect && (
        <div style={{ fontSize: 11, color: "var(--audit-text-muted)", marginTop: 3 }}>
          '이 후보의 질문 보기'를 누르면 관련 선택지를 강조합니다. 상품 사실을 확인하기 전에는 답이나 판정에 반영하지 않습니다.
        </div>
      )}
      {others.length > 0 && !showOthers && (
        <button
          type="button"
          onClick={() => setShowOthers(true)}
          style={{ ...optionButtonStyle(false), borderStyle: "dashed", color: "var(--audit-text-muted)", marginTop: 4 }}
        >
          다른 류 결정례 {otherCount}건 보기
        </button>
      )}
      {others.length > 0 && showOthers && (
        <ul style={{ margin: "4px 0 0", paddingLeft: 19, color: "var(--audit-text-muted)" }}>
          {others.map((row) => <PrecedentHintRow key={row.hsk} row={row} />)}
        </ul>
      )}
    </div>
  );
}

/**
 * 트리가 류를 못 골라 shadow 가 없을 때, 제안 카드 신호(LLM·결정례·명칭·사무소)만으로 그리는 지도 + 더 묻기.
 * 후보는 전부 미확정·제안이고 답할 사실이 없다(가르는 사실 열은 안내 문구). 호 상세는 후보 호마다 한 번 불러 소호·세번을 채운다.
 */
/**
 * LLM 지도(두 모델 교차 출력) 호출 — 종량제 Gemini+Opus 이므로 `enabled` 일 때만, 답한 사실·검색어가 바뀔 때만 다시 부른다(서버 캐시 24시간).
 * 실패·미구성은 조용히 null(fail-open). `refresh`는 같은 요청을 다시 보낸다(캐시 적중이면 비용 없음).
 */
function useLlmMap({ q, enabled, answers, productDetails, liveCodes, liveNames, questionKeys, fetchImpl }) {
  const [llmMap, setLlmMap] = useState(null);
  const [llmLoading, setLlmLoading] = useState(false);
  const [tick, setTick] = useState(0);
  const factsKey = JSON.stringify(answers || {});
  const keysKey = (questionKeys || []).map((k) => k.key).join(",");
  useEffect(() => {
    if (!enabled) { setLlmMap(null); return undefined; }
    const ctrl = new AbortController();
    setLlmLoading(true);
    fetchBranchMap({ query: q, facts: answers, productDetails, liveCodes, liveNames, questionKeys, signal: ctrl.signal, fetchImpl }).then((res) => {
      if (ctrl.signal.aborted) return;
      setLlmMap(res.available ? res.map : null);
      setLlmLoading(false);
    });
    return () => { ctrl.abort(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, q, factsKey, keysKey, tick]);
  return { llmMap, llmLoading: enabled && llmLoading, refreshLlm: () => setTick((t) => t + 1) };
}

/** 제안 신호의 LLM 법령어 품명 — 검색어와 같은 글자면 쓸모없으니 비운다. */
function llmNameOf(proposal, q) {
  const n = String(proposal?.signals?.llm?.name_ko || "").trim().slice(0, 40);
  if (!n) return "";
  return normalizeTariffName(n) === normalizeTariffName(q) ? "" : n;
}

function mergeTariffItems(base, extra) {
  const a = Array.isArray(base) ? base : [];
  const b = Array.isArray(extra) ? extra : [];
  if (!b.length) return a;
  const seen = new Set(a.map((it) => String(it?.hsk || "").replace(/\D/g, "")));
  return [...a, ...b.filter((it) => { const d = String(it?.hsk || "").replace(/\D/g, ""); if (!d || seen.has(d)) return false; seen.add(d); return true; })];
}

/** LLM 품명으로 관세율표 검색 한 번(검색어·품명당 1회). 실패·미인증은 null(fail-open). */
function useLlmNameLookup({ q, name, fetchImpl }) {
  const [state, setState] = useState(null);
  useEffect(() => {
    if (!q || !name) { setState(null); return undefined; }
    let alive = true;
    const key = `${q}|${name}`;
    (async () => {
      try {
        const token = localStorage.getItem("token");
        const r = await (fetchImpl || fetch)(`/api/onto/tariff-search?q=${encodeURIComponent(name)}&mode=keyword&limit=30`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
        if (!r || !r.ok) { if (alive) setState(null); return; }
        const j = await r.json();
        if (alive) setState({ key, name, items: Array.isArray(j?.items) ? j.items : [], nameMatches: Array.isArray(j?.name_matches) ? j.name_matches : [] });
      } catch (_) { if (alive) setState(null); }
    })();
    return () => { alive = false; };
  }, [q, name, fetchImpl]);
  return state && state.key === `${q}|${name}` ? state : null;
}

function ProposalOnlyMapBlock({ q, queryTerms = [], proposal, tariffItems, tariffMatches, headingMatches = [], precedentHints, fetchImpl, productDetails = null, onAnswer = null, disabled = false }) {
  const model = buildCandidateCompareModel({ tree: null, answers: {}, tariffItems, tariffMatches, precedentHints, proposal });
  const consensusOnly = precedentConsensus(precedentHints, []) || precedentTitleConsensus(precedentHints);   // 지도 우선 세번(결정례)
  const brokerHistory = useHistorySignal(q);   // 57차: 관세사 신고이력 분포(이력 탭이 게시)
  const sources = useMemo(() => sourceCodes({ tariffMatches, headingMatches, precedentHints, brokerHistory }), [tariffMatches, headingMatches, precedentHints, brokerHistory]);
  // 트리가 류를 못 골랐으니 LLM 지도(두 모델)는 자동으로 부른다 — 여기서는 주역이다. 서버 캐시 24시간.
  const { llmMap, llmLoading, refreshLlm } = useLlmMap({ q, enabled: model.available && isLlmMapVisible(), answers: {}, productDetails, liveCodes: [], liveNames: {}, questionKeys: [], fetchImpl });
  const hs4s = model.available ? [...new Set([...model.candidates.map((c) => c.code.slice(0, 4)), ...sourceHeadings(sources).map((h) => h.hs4)])] : [];
  const [details, setDetails] = useState({});
  useEffect(() => {
    if (!hs4s.length) return undefined;
    let alive = true;
    hs4s.forEach((h) => {
      if (headingDetailCache.has(h)) { if (!details[h]) setDetails((s) => ({ ...s, [h]: headingDetailCache.get(h) })); return; }
      Promise.resolve().then(() => (fetchImpl || fetch)(`/api/onto/heading-detail?hs=${h}&lang=kor`)).then((r) => (r && r.ok ? r.json() : null))
        .then((json) => { if (!json) return; headingDetailCache.set(h, json); if (alive) setDetails((s) => ({ ...s, [h]: json })); })
        .catch(() => { /* fail-open */ });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hs4s.join(",")]);
  const [selected, setSelected] = useState(null);
  const [flash, setFlash] = useState(null);
  // 대화 열(결과 왼쪽)에 문맥을 준다 — 이 경우 트리 질문이 없어 채우기 칩은 없고 살아 있는 후보만.
  const ctxKey = model.available ? model.candidates.map((c) => `${c.code}:${c.name || ""}`).join("|") : "";
  useEffect(() => {
    if (!model.available) return undefined;
    publishAskContext({ q, facts: {}, liveCodes: model.candidates.map((c) => c.code), liveNames: Object.fromEntries(model.candidates.filter((c) => c.name).map((c) => [c.code, c.name])), hs4s, questionKeys: [], narrowed: null, disabled, source: "proposal_only" });
    return () => clearAskContext(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, ctxKey, disabled]);
  useAskFlash(q, useCallback((code) => setFlash(`${code}#${Date.now()}`), []));
  const [findTerm, setFindTerm] = useState(null);
  useAskFind(q, useCallback((term) => setFindTerm(`${term}#${Date.now()}`), []));
  if (!model.available) return null;
  const map = buildBranchMap({ candidates: model.candidates, groups: model.groups, details, tree: null, answers: {}, llmMap, query: q, extraHeadings: sourceHeadings(sources) });
  const confirmDescription = onAnswer ? (summary, models) => { recordMapAdoption(map.llm?.requestHash, models, fetchImpl || fetch); onAnswer("product_description", summary); } : null;
  return (
    <div data-testid="proposal-only-map" style={{ marginTop: 10 }}>
      <BranchMapView map={map} candidates={model.candidates} checklistOpen={0} disabled={disabled} onAnswer={() => {}} onAnswerMany={() => {}} flashCode={flash} findTerm={findTerm} queryTerms={queryTerms} selected={selected} onSelect={setSelected}
        preferredCode={consensusOnly ? consensusOnly.hsk : null} preferredWhy={consensusOnly ? (consensusOnly.title ? "결정례 제목 일치" : "결정례 합의") : ""} brokerHistory={brokerHistory} sources={sources}
        fetchConditions={(code) => fetchTargetConditions({ code, facts: {}, token: localStorage.getItem("token"), fetchImpl: fetchImpl || fetch })}
        onConfirmDescription={confirmDescription} onRefreshLlm={refreshLlm} llmLoading={llmLoading} />
    </div>
  );
}

function DecisionTreeCard({ tree, precedentHints, shadow, answers, previousFacts, recomputing, error, onAnswer, onAnswerMany, onRemoveAnswer, onOpenProve, onResearchReady, onChooseSignalHeading = null, onChooseProveSubheading = null, signalNote = null, fetchImpl, q, tariffMatches, headingMatches = [], tariffItemsAll, proposal = null, productDetails = null, queryTerms = [] }) {
  // 인식된 사실 칩을 눌러 값을 바꾸거나, 인식되지 않은 축(차폐 여부 등)을 직접 넣는다.
  const [editingKey, setEditingKey] = useState(null);
  // 후보는 질문의 선택지를 강조할 뿐 확인 사실이나 판정 입력으로 바꾸지 않는다.
  const [candidateHsk, setCandidateHsk] = useState(null);
  const [researchPhase, setResearchPhase] = useState("idle");
  // 표시 방식 전환: "지도"(기본, 물품→가르는 사실→후보 세번 흐름) | "표"(관세율표 배치) | "질문 목록"(기존). 판정·저장 키는 같다. 후보가 없으면 질문 목록으로 떨어진다.
  const [viewMode, setViewMode] = useState(readViewPref);
  // 검색 결과에 현재 호의 잎이 없으면(방호복 검색은 4015만 올 수 있다) 호 상세를 한 번 불러 표·후보를 채운다. 실패해도 카드는 그대로(fail-open).
  const [headingDetail, setHeadingDetail] = useState(null);
  const wantsCandidates = viewMode === "map" || viewMode === "table";
  const missingHs4 = wantsCandidates ? needsHeadingDetail(tree, tariffItemsAll) : null;
  useEffect(() => {
    if (!missingHs4 || (headingDetail && headingDetail.hs4 === missingHs4)) return undefined;
    let alive = true;
    const failed = { hs4: missingHs4, items: [], hs6Labels: {}, headingLabel: null, failed: true };
    Promise.resolve()
      .then(() => (fetchImpl || fetch)(`/api/onto/heading-detail?hs=${missingHs4}&lang=kor`))
      .then((r) => (r && r.ok ? r.json() : null))
      .then((json) => { if (alive) setHeadingDetail(json ? headingDetailToItems(json) : failed); })
      .catch(() => { if (alive) setHeadingDetail(failed); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [missingHs4, q]);
  const compareModel = wantsCandidates
    ? buildCandidateCompareModel({ tree, answers, tariffItems: tariffItemsAll, tariffMatches, precedentHints, headingDetail, proposal })
    : { available: false };
  if (compareModel.available && compareModel.sheet.reason === "search_items_missing" && missingHs4 && !(headingDetail && headingDetail.hs4 === missingHs4)) compareModel.sheet.reason = "loading";
  const narrowCard = useNarrowViewport(); // 휴대폰: 도구줄을 줄바꿈하고 기술 설명(후보 경로·검수)은 숨긴다
  const mapActive = viewMode === "map" && compareModel.available;
  const tableActive = viewMode === "table" && compareModel.available;
  const structured = mapActive || tableActive;
  const switchView = (mode) => { setViewMode(mode); writeViewPref(mode); };
  // 표 보기: 후보 호마다 관세율표 상세를 한 번 불러 전체 구조(소호·군·국내 세번)를 그린다. 캐시는 모듈 수준.
  const [headingDetails, setHeadingDetails] = useState({});
  // 트리가 보지 않은 호도 상세를 불러 소호·국내 세번까지 그린다(2026-09-28 사용자: "6214 를 누르면 소호·세번까지 이어져야 가늠이 된다")
  // — LLM 후보 호·관세사 이력 1위의 호·관세율표 호 명칭 일치. 점선(미인식)으로 그리며 판정과 무관하다.
  const historyForDetails = useHistorySignal(q);
  // 출처별 세번의 결정례 세번(합의·제목 일치·최다) — 그 호도 지도에 그려야 결정례 줄을 눌렀을 때 갈 마디가 있다.
  const precedentSourceCode = sourceCodes({ precedentHints }).rows.find((r) => r.kind === "precedent")?.code || "";
  const extraHeadingList = [
    ...(precedentSourceCode ? [{ hs4: precedentSourceCode.slice(0, 4), why: "국내 결정례의 호" }] : []),
    ...(historyForDetails?.top?.hsk ? [{ hs4: String(historyForDetails.top.hsk).replace(/\D/g, "").slice(0, 4), why: "관세사 이력 1위의 호" }] : []),
    ...(precedentHints?.model_proposal?.hsk ? [{ hs4: String(precedentHints.model_proposal.hsk).replace(/\D/g, "").slice(0, 4), why: "PROVE 모델 제안의 호" }] : []),
    ...(Array.isArray(headingMatches) ? headingMatches.map((h) => ({ hs4: String(h?.code || "").replace(/\D/g, "").slice(0, 4), why: "관세율표 명칭 일치" })) : []),
  ].filter((h) => h.hs4.length === 4);
  const extraHs4s = structured ? [
    ...(proposalLlmMap(proposal)?.candidates || []).map((c) => c.code),
    historyForDetails?.top?.hsk,
    precedentSourceCode,
    precedentHints?.model_proposal?.hsk,
    ...(Array.isArray(headingMatches) ? headingMatches.map((h) => h?.code) : []),
  ].map((c) => String(c || "").replace(/\D/g, "").slice(0, 4)).filter((h) => h.length === 4).slice(0, 6) : [];
  const candidateHs4s = structured ? [...new Set([...compareModel.candidates.map((c) => c.code.slice(0, 4)), ...extraHs4s])] : [];
  useEffect(() => {
    if (!structured) return undefined;
    let alive = true;
    const missing = candidateHs4s.filter((h) => !headingDetails[h] && !headingDetailCache.has(h));
    candidateHs4s.forEach((h) => { if (headingDetailCache.has(h) && !headingDetails[h]) setHeadingDetails((s) => ({ ...s, [h]: headingDetailCache.get(h) })); });
    missing.forEach((h) => {
      Promise.resolve().then(() => (fetchImpl || fetch)(`/api/onto/heading-detail?hs=${h}&lang=kor`)).then((r) => (r && r.ok ? r.json() : null))
        .then((json) => { if (!json) return; headingDetailCache.set(h, json); if (alive) setHeadingDetails((s) => ({ ...s, [h]: json })); })
        .catch(() => { /* fail-open: 그 호는 "불러오고 있습니다" 문구로 남는다 */ });
    });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [structured, candidateHs4s.join(",")]);
  // LLM 지도(두 모델 교차 출력) — 종량제라 트리가 살아 있는 후보를 못 냈을 때(제안 신호만·방향 철회)만 자동, 그 밖에는 "두 모델로 검토" 버튼.
  const [llmWanted, setLlmWanted] = useState(false);
  useEffect(() => { setLlmWanted(false); }, [q]);
  const llmMapVisible = isLlmMapVisible(); // 기본 숨김 — verdictTab.js
  const llmAuto = llmMapVisible && structured && (compareModel.mode === "proposal_only" || compareModel.mode === "withdrawn");
  const llmEnabled = structured && (llmAuto || llmWanted);
  const llmQuestionKeys = structured ? [
    ...compareModel.groups.choices.filter((c) => !String(c.key).startsWith("obs_note_listed_")).map((c) => ({ key: c.key, prompt: c.prompt || "", values: c.options.map((o) => o.value), labels: Object.fromEntries(c.options.map((o) => [o.value, o.label])) })),
    ...compareModel.groups.checklist.map((c) => ({ key: c.key, prompt: c.prompt || "", values: ["yes", "no"], labels: { yes: "예", no: "아니오" } })),
  ] : [];
  const llmLiveCodes = structured ? compareModel.candidates.filter((c) => c.state !== "excluded").map((c) => c.code) : [];
  const llmLiveNames = structured ? Object.fromEntries(compareModel.candidates.filter((c) => c.state !== "excluded" && c.name).map((c) => [c.code, c.name])) : {};
  const { llmMap, llmLoading, refreshLlm } = useLlmMap({ q, enabled: llmEnabled, answers, productDetails, liveCodes: llmLiveCodes, liveNames: llmLiveNames, questionKeys: llmQuestionKeys, fetchImpl });
  const [flashCode, setFlashCode] = useState(null);
  // 지도에서 고른 후보(역검증 대상). 재계산으로 지도가 다시 그려져도 유지되고, 검색어가 바뀌면 지운다. 저장이 아니다.
  const [mapSelected, setMapSelected] = useState(null);
  useEffect(() => { setMapSelected(null); }, [q]);
  // 제안 카드의 LLM 후보를 proposal/map 대신 쓴다(같은 모양: 트리가 모르는 호는 점선 마디). 제안일 뿐 상태를 정하지 않는다.
  const llmMapEffective = llmMap || proposalLlmMap(proposal);
  const branchTable = tableActive ? buildBranchTable({ candidates: compareModel.candidates, groups: compareModel.groups, details: headingDetails, llmMap: llmMapEffective, extraHeadings: extraHeadingList }) : null;
  useEffect(() => { setCandidateHsk(null); }, [q]);
  // 일반 합의(3건·80%)가 없으면 제목 일치 합의(검색어와 같은 제목의 사례가 한 세번으로만) — 2026-09-15.
  const consensus = precedentConsensus(precedentHints, treeScopePrefixes(tree)) || precedentTitleConsensus(precedentHints);
  const brokerHistory = useHistorySignal(q);   // 57차: 관세사 신고이력 분포(이력 탭이 게시) → 지도 배지·추천 길
  // 출처별 세번(2026-09-28): 관세율표·결정례·관세사 이력이 가리키는 세번 — 지도 물품 칸에 나란히, 두 출처 이상 일치할 때만 자동 길.
  const sources = useMemo(() => sourceCodes({ tariffMatches, headingMatches, precedentHints, brokerHistory }), [tariffMatches, headingMatches, precedentHints, brokerHistory]);
  const consensusState = consensusRelation(consensus, tree);
  const tariffClassified = classifyTariffMatches(tariffMatches, treeScopePrefixes(tree));
  const tariffSingle = tariffClassified.kind === "single_in_scope" ? tariffClassified.matches[0] : null;
  const tariffRelation = tariffSingle ? consensusRelation({ hsk: tariffSingle.hsk }, tree) : "none";
  const meta = treeStatusMeta(tree);
  // 질문·편집 축이 내려준 표시 라벨을 사실 칩에도 쓴다(영문 family 키 대신 "7318 · 한글 호명").
  const optionLabelIndex = {};
  [...(Array.isArray(tree.questions) ? tree.questions : []), ...(Array.isArray(tree.editable_facts) ? tree.editable_facts : [])]
    .forEach((entry) => {
      if (entry && entry.fact_key && entry.option_labels && typeof entry.option_labels === "object") {
        optionLabelIndex[entry.fact_key] = { ...(optionLabelIndex[entry.fact_key] || {}), ...entry.option_labels };
      }
    });
  if (tree.fact_value_labels && typeof tree.fact_value_labels === "object") {
    Object.entries(tree.fact_value_labels).forEach(([key, labels]) => {
      if (labels && typeof labels === "object") optionLabelIndex[key] = { ...(optionLabelIndex[key] || {}), ...labels };
    });
  }
  const displayValue = (key, value) => (optionLabelIndex[key] && optionLabelIndex[key][String(value)]) || valueLabel(value, key);
  const branchMap = mapActive ? buildBranchMap({ candidates: compareModel.candidates, groups: compareModel.groups, details: headingDetails, tree, answers, optionLabels: optionLabelIndex, llmMap: llmMapEffective, query: q, extraHeadings: extraHeadingList }) : null;
  // 더 묻기 문맥은 결과 왼쪽 대화 열(AskDrawer)로 게시한다: 현재 질문 키·옵션값(서버가 채우기 칩을 이것으로 검증), 살아 있는 후보의 명칭.
  // 호·소호 선택 질문은 넣지 않는다 — 법적 판단 키라 저장소가 거르고, 사용자가 고르는 것도 아니다.
  const askQuestionKeys = structured ? [
    ...compareModel.groups.choices.filter((c) => !String(c.key).startsWith("obs_note_listed_")).map((c) => ({ key: c.key, values: c.options.map((o) => o.value) })),
    ...compareModel.groups.checklist.map((c) => ({ key: c.key, values: ["yes", "no"] })),
  ] : [];
  const askLiveCodes = structured ? compareModel.candidates.filter((c) => c.state !== "excluded").map((c) => c.code) : [];
  const askLiveNames = structured ? Object.fromEntries(compareModel.candidates.filter((c) => c.state !== "excluded" && c.name).map((c) => [c.code, c.name])) : {};
  const askFacts = Object.fromEntries(Object.entries(answers || {}).filter(([k]) => !String(k).startsWith("_")));
  const askNarrowed = (branchMap || branchTable)?.narrowed || null;
  const askCtxKey = JSON.stringify([askFacts, askLiveCodes, askQuestionKeys.map((k) => k.key), candidateHs4s, askNarrowed, recomputing]);
  useEffect(() => {
    publishAskContext({ q, facts: askFacts, liveCodes: askLiveCodes, liveNames: askLiveNames, hs4s: candidateHs4s, questionKeys: askQuestionKeys, narrowed: askNarrowed, disabled: recomputing, source: structured ? "tree" : "tree_questions" });
    return () => clearAskContext(q);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, askCtxKey]);
  useAskFlash(q, useCallback((code) => { setFlashCode(`${code}#${Date.now()}`); }, []));
  // 지도·표 공용 찾기 낱말 — 카드 머리의 칸이 원천이고, 대화 열의 "X 찾아줘"도 여기로 들어온다.
  const [findTerm, setFindTerm] = useState("");
  useEffect(() => { setFindTerm(""); }, [q]);
  useAskFind(q, useCallback((term) => { setFindTerm(String(term || "")); }, []));
  const editable = (Array.isArray(tree.editable_facts) ? tree.editable_facts : [])
    .filter((entry) => entry && entry.fact_key && isProductQuestion(entry) && Array.isArray(entry.options) && entry.options.length > 0);
  const editableByKey = new Map(editable.map((entry) => [entry.fact_key, entry]));
  const action = String(tree.action || "");
  const pathParts = [
    tree.supported_chapter && `제${tree.supported_chapter}류`,
    tree.supported_hs4 && `${tree.supported_hs4}호`,
    tree.supported_hs6 && formatSubdivisionHsk(tree.supported_hs6),
    tree.supported_hsk && formatSubdivisionHsk(tree.supported_hsk),
  ].filter(Boolean);
  const hs4Candidates = Array.isArray(tree.hs4_candidates) ? tree.hs4_candidates : [];
  const hs6Candidates = Array.isArray(tree.hs6_candidates) ? tree.hs6_candidates : [];

  const rawFacts = normalizeTreeFacts(tree.facts, answers)
    .filter((fact) => fact && fact.key && fact.value !== null && fact.value !== undefined && fact.value !== "")
    .filter((fact) => !String(fact.key).startsWith("_") && !isLegalDecisionKey(fact.key));
  const hasArticleRole = rawFacts.some((fact) => fact.key === "article_role");
  // item_role은 article_role의 별칭이라 같은 값이면 한 번만 보인다.
  const facts = rawFacts.filter((fact) => !(fact.key === "item_role" && hasArticleRole));
  const answeredEntries = Object.entries(answers || {})
    .filter(([key, value]) => !isLegalDecisionKey(key) && value !== null && value !== undefined && value !== "");
  const factKeys = new Set(facts.map((fact) => fact.key));
  const missingEditable = action === "out_of_scope"
    ? []
    : editable.filter((entry) => !factKeys.has(entry.fact_key) && entry.current === undefined);
  const editingEntry = editingKey ? editableByKey.get(editingKey) : null;
  const editingCurrent = editingEntry
    ? (answers[editingKey] !== undefined ? String(answers[editingKey]) : String(editingEntry.current ?? ""))
    : "";

  const dialogue = productDialogue(tree);
  const allQuestions = [...dialogue.questions, ...(dialogue.deferred_questions || [])];
  const questions = dialogue.questions.map(question => ({ ...question,
    key: question.fact_key || question.key,
    prompt: question.prompt_ko || question.question || question.label,
  }));
  // The current product dialogue owns pending questions and unknown/resume state.
  // A separate legacy subdivision preview can still contain unanswered fields
  // after the product contract has already supplied those facts and reached KR10.
  const useLegacyDomesticQuestions = !tree.product_fact_dialogue
    && digitsOf(tree.supported_hsk).length !== 10 && dialogue.status !== 'needs_documents';
  const deferredCount = allQuestions.length - questions.length
    + (useLegacyDomesticQuestions && shadow && Array.isArray(shadow.deferred_questions) && digitsOf(shadow.hs6) === treeHs6Of(tree)
      ? shadow.deferred_questions.length : 0);

  // 레거시 KR10 결정표 질문은 트리가 확정한 소호와 같은 hs6일 때만 그 아래에서 이어 묻는다.
  // 다른 소호의 질문(기존 분류기가 고른 번호 기준)은 표시하지 않는다.
  const treeHs6 = digitsOf(tree.supported_hs6);
  const promptSet = new Set(questions.map((question) => question.prompt));
  const domesticQuestions = useLegacyDomesticQuestions && shadow && treeHs6 && digitsOf(shadow.hs6) === treeHs6
    && Array.isArray(shadow.questions)
    ? shadow.questions.filter((text) => typeof text === "string" && text && !isLegalDecisionText(text) && !promptSet.has(text))
    : [];

  let pathValue;
  if (dialogue.conditional_assessment) {
    pathValue = "조건부 검토 방향 도출 · 아래 근거와 미확인 조건을 확인하세요";
  } else if (dialogue.product_intake?.kind === 'source_direction' && dialogue.status !== 'candidate_ready') {
    pathValue = dialogue.status === 'needs_documents' ? "자료 확인 대기 · 질문 중단"
      : dialogue.progress?.blocker === 'engine_coverage' ? "엔진의 분류 규칙 추가 연결 필요" : "상품 조건 확인 필요";
  } else if (pathParts.length) {
    pathValue = pathParts.map((part, index) => (
      <React.Fragment key={`${part}-${index}`}>
        {index > 0 && <span style={{ margin: "0 5px", color: "var(--audit-text-muted)" }}>→</span>}
        <span>{part}</span>
      </React.Fragment>
    ));
  } else if (hs6Candidates.length > 1) {
    pathValue = `후보 소호 ${hs6Candidates.length}개`;
  } else if (hs4Candidates.length > 1) {
    pathValue = `후보 호 ${hs4Candidates.length}개`;
  } else if (dialogue.conditional_assessment) {
    pathValue = "조건부 검토 방향 도출 · 아래 근거와 미확인 조건을 확인하세요";
  } else if (action === "out_of_scope") {
    pathValue = "범위 밖";
  } else {
    pathValue = dialogue.conditional_assessment ? "조건부 검토 방향 도출 · 아래 근거와 미확인 조건을 확인하세요"
      : dialogue.progress?.blocker === 'engine_coverage' ? "엔진의 분류 규칙 추가 연결 필요"
      : dialogue.status === 'needs_documents' ? "자료 확인 대기 · 질문 중단" : "판별 사실 필요";
  }

  if (recomputing || error) return <div data-testid="decision-tree-preview-card" style={{...cardStyle,borderLeft:`4px solid ${GREY.border}`}}>
    <CardHeader title="PROVE 결정트리 사전 점검" meta={{label: recomputing ? '답변 반영 중' : '재계산 필요', ...GREY}} />
    <div role="status" style={{marginTop:12}}>
      <strong>{recomputing ? '확인한 사실로 결정트리를 다시 계산하고 있습니다' : '다시 계산하지 못했습니다. 확인한 답변은 저장했습니다.'}</strong>
      <p>현재 답변을 반영한 결과가 나올 때까지 이전 후보와 질문은 표시하지 않습니다.</p>
      {!recomputing && <button type="button" onClick={() => onAnswerMany({})}>저장한 답변으로 다시 계산</button>}
    </div>
  </div>;

  return (
    <div
      data-testid="decision-tree-preview-card"
      style={{ ...cardStyle, border: "1px solid var(--audit-border)", borderLeft: `4px solid ${meta.border}` }}
    >
      <CardHeader title="PROVE 결정트리 사전 점검" meta={meta} />
      <div role="tablist" aria-label="표시 방식" style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 8, alignItems: "center" }}>
        <button type="button" role="tab" aria-selected={mapActive} onClick={() => switchView("map")} style={optionButtonStyle(mapActive)}>지도</button>
        <button type="button" role="tab" aria-selected={tableActive} onClick={() => switchView("table")} style={optionButtonStyle(tableActive)}>표</button>
        {structured && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, marginLeft: 6 }}>
            <input data-testid="card-find" aria-label="지도·표에서 찾기" value={findTerm} onChange={(e) => setFindTerm(e.target.value)} placeholder={narrowCard ? "찾기" : "이름으로 찾기 (예: 김)"}
              onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); requestFindStep(e.shiftKey ? -1 : 1); } else if (e.key === "Escape") { setFindTerm(""); } }}
              style={{ width: narrowCard ? 90 : 150, border: "1px solid var(--audit-border-strong, #cdd1d8)", borderRadius: 6, padding: "3px 8px", font: "inherit", fontSize: 12, background: findTerm.trim() ? "#fef3c7" : "var(--audit-bg)", color: "inherit" }} />
            {findTerm && <button type="button" aria-label="찾기 지우기" onClick={() => setFindTerm("")} style={{ border: 0, background: "none", color: "var(--audit-text-muted)", cursor: "pointer", fontFamily: "inherit" }}>✕</button>}
          </span>
        )}
        {/* 질문 목록은 후보가 없을 때의 대체 화면으로만 남는다(2026-09-14 사용자 결정: 탭 삭제). 설명 입력 질문은 표·지도 아래 접힘에서 답한다. */}
        {!structured && <button type="button" role="tab" aria-selected={!structured} onClick={() => switchView("questions")} style={optionButtonStyle(!structured)}>질문 목록</button>}
        {wantsCandidates && !compareModel.available && (
          <span style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>후보 세번이 아직 없어 질문 목록으로 보입니다.</span>
        )}
        {tree.candidate_route && !narrowCard && (
          <span data-testid="candidate-route-note" style={{ fontSize: 11, color: "var(--audit-text-muted)" }} title={tree.candidate_route.note || ""}>
            후보 호 <b style={{ fontFamily: "JetBrains Mono, monospace" }}>{tree.candidate_route.hs4}</b>({candidateSourceLabel(tree.candidate_route.source)})로 결정트리를 열었습니다
            {Array.isArray(tree.candidate_route.displaced_weak_wake) && tree.candidate_route.displaced_weak_wake.length > 0 && (
              <span data-testid="weak-wake-note"> · 글자만으로 깨어난 제{tree.candidate_route.displaced_weak_wake.map((d) => d.chapter).join("·")}류 트리는 물품과 맞지 않아 대신 열었습니다</span>
            )}
            {Array.isArray(tree.candidate_route.displaced_keyword_wake) && tree.candidate_route.displaced_keyword_wake.length > 0 && (
              <span data-testid="keyword-wake-note"> · 검색어 글자로 깨어난 제{[...new Set(tree.candidate_route.displaced_keyword_wake.map((d) => d.chapter).filter(Boolean))].join("·") || "?"}류 트리는 뒤로 물렸습니다</span>
            )}
            {" "}· 힌트는 사실이 아님 · 확정 아님
          </span>
        )}
        {tree.candidate_route && !narrowCard && <CandidateScreening route={tree.candidate_route} />}
        {tree.candidate_route?.subheading_proposal && (
          <SubheadingProposal proposal={tree.candidate_route.subheading_proposal} disabled={recomputing} onApply={onChooseProveSubheading} />
        )}
        {structured && compareModel.mode === "proposal_only" && (
          <span data-testid="proposal-only-hint" style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>
            {tree?.observable_derivation?.kind === "observable_decomposition"
              ? "상품 질문에 답하면 지도에 길이 이어집니다 · 지금 후보는 제안 카드 신호(미확정)"
              : "결정트리 후보가 없어 제안 카드 신호로 그렸습니다 · 전부 미확정"}
          </span>
        )}
        {llmMapVisible && structured && !llmAuto && !llmEnabled && (
          <button type="button" data-testid="llm-map-button" onClick={() => setLlmWanted(true)} disabled={recomputing} title="Gemini와 Opus에 같은 물품을 읽혀 후보와 가르는 사실을 나란히 봅니다 · 종량제 호출 · 확정 아님"
            style={{ marginLeft: "auto", border: "1px dashed var(--audit-border-strong, #cdd1d8)", borderRadius: 6, padding: "2px 9px", fontSize: 11, background: "transparent", color: "var(--audit-text-muted)", fontFamily: "inherit", cursor: "pointer" }}>
            LLM 두 모델로 검토 (Gemini + Opus)
          </button>
        )}
        {structured && llmEnabled && llmLoading && !llmMap && (
          <span data-testid="llm-map-loading" style={{ marginLeft: "auto", fontSize: 11, color: "var(--audit-text-muted)" }}>Gemini·Opus 검토 중…</span>
        )}
      </div>
      {Object.keys(previousFacts || {}).some((key) => !(key in answers)) && (
        <div style={{ marginTop: 8, fontSize: 12 }} data-testid="previous-facts-review">
          이전 답변에는 자동 추천이 포함될 수 있어 재확인이 필요합니다. 현재 질문에 답한 사실만 판정에 사용합니다.
        </div>
      )}

      {(recomputing || researchPhase === "loading_research" || researchPhase === "loading_project") && (
        <ProgressCard
          inner
          title={recomputing
            ? "확인한 사실로 결정트리를 다시 계산하고 있습니다"
            : researchPhase === "loading_research"
              ? "AI 웹검증으로 제품을 조사하고 있습니다"
              : "웹검증 결과를 결정트리 질문에 맞추고 있습니다"}
          detail={recomputing
            ? "저장된 답을 실어 류·호·소호·국내세분을 다시 계산합니다."
            : researchPhase === "loading_research"
              ? "제조사·공식 자료를 찾아 제품 설명을 만듭니다. 첫 조회는 20초 이상 걸릴 수 있습니다."
              : "제품 설명에서 위 질문에 대응하는 근거 문장을 찾습니다. 확인 전에는 답으로 쓰지 않습니다."}
          steps={[
            { label: "결정트리 사전 점검", state: "done" },
            { label: "AI 웹검증 제품 조사", state: recomputing ? "done" : researchPhase === "loading_research" ? "active" : "done" },
            { label: "질문에 맞춰 제안", state: recomputing ? "done" : researchPhase === "loading_project" ? "active" : "pending" },
            { label: "확인한 사실로 재계산", state: recomputing ? "active" : "pending" },
          ]}
        />
      )}

      {consensus && (
        <PrecedentConsensusBanner
          consensus={consensus}
          relation={consensusState}
          remaining={questions.length}
          tariffAgrees={Boolean(tariffSingle) && tariffSingle.hsk === consensus.hsk}
          onOpenProve={onOpenProve}
        />
      )}
      {tariffClassified.kind !== "none" && !(consensus && tariffSingle && tariffSingle.hsk === consensus.hsk) && (
        <TariffNameBanner
          classified={tariffClassified}
          tree={tree}
          relation={tariffRelation}
          remaining={questions.length}
          precedentHints={precedentHints}
          fetchImpl={fetchImpl}
          recomputing={recomputing}
          onSelect={(hsk) => setCandidateHsk(hsk)}
          onOpenProve={onOpenProve}
        />
      )}

      {candidateHsk && <div role="status" style={{ marginTop: 8, fontSize: 12 }}>
        {formatSubdivisionHsk(candidateHsk)} 후보를 참고용으로 선택했습니다. 상품 사실과 규칙의 검증이 필요합니다.
      </div>}
      <div style={gridStyle}>
        <ResultCell label="결정트리 경로" value={recomputing ? "답변을 반영해 다시 계산 중입니다" : pathValue} />
      </div>
      {tree?.signal_assumption && (
        <div data-testid="signal-assumption-note" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.6, color: "var(--audit-text-muted)" }}>
          {tree.signal_assumption.basis === "prove_subheading"
            ? <>PROVE 가 제안해 고른 소호 <b style={{ color: "var(--audit-text)" }}>{formatHs6(tree.signal_assumption.hs6)}</b>로 가정해 계산했습니다</>
            : tree.signal_assumption.basis === "user_alternative"
            ? <>고른 후보 호 <b style={{ color: "var(--audit-text)" }}>{tree.signal_assumption.hs4}</b>로 가정해 계산했습니다</>
            : <>LLM 후보와 관세사 신고이력이 모두 <b style={{ color: "var(--audit-text)" }}>{tree.signal_assumption.hs4}</b>호를 가리켜 그 호로 가정해 계산했습니다</>}
          {" · 조건부 · 확정 아님"}
          {signalNote && <span data-testid="signal-note" style={{ marginLeft: 6 }}>· {signalNote.hs4}호는 이 결정트리가 가정 계산하지 못합니다(지도·관세사 이력을 참고하세요)</span>}
          <SignalNoteConfirm tree={tree} answers={answers} onAnswer={onAnswer} fetchImpl={fetchImpl} disabled={recomputing} />
          {Array.isArray(tree.signal_assumption.alternatives) && tree.signal_assumption.alternatives.length > 0 && onChooseSignalHeading && (
            <span style={{ marginLeft: 6 }}>
              · 다른 후보로 계산:{" "}
              {tree.signal_assumption.alternatives.map((h) => (
                <button key={h} type="button" disabled={recomputing} onClick={() => onChooseSignalHeading(h)}
                  style={{ marginLeft: 4, border: "1px solid var(--audit-border)", borderRadius: 999, padding: "0 8px", fontSize: 11, background: "transparent", color: "#1d4ed8", cursor: "pointer", fontFamily: "inherit" }}>{h}</button>
              ))}
            </span>
          )}
        </div>
      )}

      {action === "out_of_scope" && !dialogue.product_intake && (
        <div style={{ marginTop: 8, fontSize: 12, lineHeight: 1.55, color: "var(--audit-text-muted)" }}>
          {outOfScopeExplanation(tree.reason)}
          {(tree.outside_candidate_hs6 || tree.outside_candidate_hs4) && (
            <> 다른 류의 후보 <b>{tree.outside_candidate_hs6 ? formatSubdivisionHsk(tree.outside_candidate_hs6) : `${tree.outside_candidate_hs4}호`}</b>를 확인하세요.</>
          )}
          {" "}결정트리 질문을 만들지 않으며 기존 검색 결과는 그대로 유지됩니다.
        </div>
      )}

      {/* 관측 분해에서 이미 답한 질문 — 지도·표 모드에서도 무엇을 답했는지 보이고 ✕로 지울 수 있게(지우면 그 뒤 답은 엔진이 무효로 돌린다). */}
      {structured && (tree?.observable_derivation?.answered || []).length > 0 && (
        <div data-testid="observable-answered" style={{ marginTop: 8, fontSize: 12, lineHeight: 1.55 }}>
          <b>지금까지 답한 것</b>
          <span style={{ marginLeft: 6, fontSize: 11, color: "var(--audit-text-muted)" }}>
            이 검색어에 저장됩니다. ✕로 지우면 그 질문부터 다시 묻습니다.
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
            {tree.observable_derivation.answered.map((a) => (
              <span key={`obs-answer-${a.fact_key}`} data-testid="observable-answered-chip" title={a.prompt_ko} style={factChipStyle(a.value !== "unknown")}>
                <span style={{ color: "var(--audit-text-muted)" }}>{shortLabel(a.prompt_ko, 28)}</span>
                <b title={a.label}>{shortLabel(a.label)}</b>
                <button
                  type="button"
                  disabled={recomputing}
                  aria-label={`${a.prompt_ko} 답 지우기`}
                  onClick={() => onRemoveAnswer?.(a.fact_key)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: "var(--audit-text-muted)", fontFamily: "inherit", fontSize: 12 }}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* 결정트리가 아직 상품 질문을 내고 있으면(관측 분해 — 첫 질문·함량·국내 세분 등) 지도·표가 그려져도 그 질문을 위에 함께 보인다. */}
      {/* 2026-09-25 사용자: 트리가 호를 못 잡은(proposal_only) 물품은 지도가 먼저다 — 그 질문은 지도 아래 한 줄 접힘(proposal-only-dialogue-fold). */}
      {/* 관측 분해 질문도 지도가 그려지면 지도 아래로 접는다(2026-09-25 사용자: 'LED 스탠드'에 의자·침구까지 늘어놓는 질문). 지도가 없을 때만 위에. */}
      {structured && tree && tree?.observable_derivation?.kind === "observable_decomposition" && !mapActive
        && ((dialogue.questions || []).length > 0 || (dialogue.document_requests || []).length > 0) && (
        <div data-testid="proposal-only-dialogue" style={{ marginTop: 8 }}>
          <ProductFactDialoguePanel key={q} tree={tree} onAnswer={onAnswer} answers={answers} disabled={recomputing} />
        </div>
      )}

      {tableActive && (
        <div data-testid="branch-table-view" style={{ marginTop: 10 }}>
          <TariffBranchTable
            table={branchTable}
            disabled={recomputing}
            onAnswer={onAnswer}
            flashCode={flashCode}
            find={findTerm}
            queryTerms={queryTerms}
            onOpenHeading={(hs4) => { try { window.open(`/?audit=heading&hs=${hs4}`, "_blank", "noopener"); } catch (_) { /* ignore */ } }}
          />
          <CandidateChecklistFold groups={compareModel.groups} disabled={recomputing} onAnswerMany={onAnswerMany} onAnswer={onAnswer} narrowed={branchTable?.narrowed || null} onOpenProve={onOpenProve} />
        </div>
      )}

      {mapActive && (
        <div data-testid="branch-map-block" style={{ marginTop: 6, minWidth: 0 }}>
          <div style={{ minWidth: 0 }}>
            <BranchMapView
              map={branchMap}
              preferredCode={consensus ? consensus.hsk : null}
              preferredWhy={consensus ? (consensus.title ? "결정례 제목 일치" : "결정례 합의") : ""}
              brokerHistory={brokerHistory}
              sources={sources}
              candidates={compareModel.candidates}
              checklistOpen={compareModel.groups.checklist.filter((c) => c.state === "open").length}
              disabled={recomputing}
              onAnswer={onAnswer}
              onAnswerMany={onAnswerMany}
              flashCode={flashCode}
              findTerm={findTerm}
              queryTerms={queryTerms}
              onFindChange={setFindTerm}
              selected={mapSelected}
              onSelect={setMapSelected}
              fetchConditions={(code) => fetchTargetConditions({ code, facts: answers, token: localStorage.getItem("token"), fetchImpl: fetchImpl || fetch })}
              answersKey={JSON.stringify(answers || {})}
              reviewFocus={compareModel.reviewFocus || ""}
              onConfirmDescription={(summary, models) => { recordMapAdoption(branchMap?.llm?.requestHash, models, fetchImpl || fetch); onAnswer("product_description", summary); }}
              onAdopt={(models) => recordMapAdoption(branchMap?.llm?.requestHash, models, fetchImpl || fetch)}
              onRefreshLlm={llmEnabled ? refreshLlm : null}
              llmLoading={llmLoading}
            />
            <CandidateChecklistFold groups={compareModel.groups} disabled={recomputing} onAnswerMany={onAnswerMany} onAnswer={onAnswer} narrowed={branchMap?.narrowed || null} onOpenProve={onOpenProve} />
          </div>
        </div>
      )}

      {structured && tree && (compareModel.mode === "proposal_only" || (mapActive && tree?.observable_derivation?.kind === "observable_decomposition"))
        && ((dialogue.questions || []).length > 0 || (dialogue.document_requests || []).length > 0) && (
        <details data-testid="proposal-only-dialogue-fold" style={{ marginTop: 10, border: "1px solid var(--audit-border)", borderRadius: 8 }}>
          <summary style={{ cursor: "pointer", padding: "8px 12px", fontSize: 12, color: "#1d4ed8", listStyle: "none" }}>
            ▸ 결정트리가 더 묻는 것 {(dialogue.questions || []).length + (dialogue.document_requests || []).length}개
            <span style={{ marginLeft: 8, fontSize: 11, color: "var(--audit-text-muted)" }}>{tree?.observable_derivation?.kind === "observable_decomposition" ? "지도의 '호를 가르는 사실'에서 눌러 답해도 됩니다 · 답하면 트리가 다시 계산합니다" : "결정트리는 아직 이 물품의 호를 정하지 못했습니다 · 답하면 트리가 다시 계산합니다"}</span>
          </summary>
          <div data-testid="proposal-only-dialogue" style={{ padding: "0 8px 8px" }}>
            <ProductFactDialoguePanel key={q} tree={tree} onAnswer={onAnswer} answers={answers} disabled={recomputing} />
          </div>
        </details>
      )}

      {!structured && (facts.length > 0 || missingEditable.length > 0) && (
        <div style={{ marginTop: 9, fontSize: 12, lineHeight: 1.55 }}>
          <b>트리가 인식한 사실</b>
          {editable.length > 0 && (
            <span style={{ marginLeft: 6, fontSize: 11, color: "var(--audit-text-muted)" }}>
              칩을 누르면 값을 바꾸거나 직접 넣을 수 있습니다
            </span>
          )}
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
            {facts.map((fact) => {
              const entry = editableByKey.get(fact.key);
              const confirmed = fact.status === "user_confirmed" && !['unknown','undetermined','edit_required',''].includes(String(fact.value ?? ''));
              const body = (
                <>
                  <span style={{ color: "var(--audit-text-muted)" }}>{factLabel(fact.key)}</span>
                  <b title={displayValue(fact.key, fact.value)}>{shortLabel(displayValue(fact.key, fact.value))}</b>
                  {confirmed && <span aria-label="사용자 확인">✓</span>}
                </>
              );
              if (!entry) return <span key={fact.key} style={factChipStyle(confirmed)}>{body}</span>;
              return (
                <button
                  type="button"
                  key={fact.key}
                  disabled={recomputing}
                  aria-label={`${factLabel(fact.key)} 값 변경`}
                  aria-expanded={editingKey === fact.key}
                  onClick={() => setEditingKey(editingKey === fact.key ? null : fact.key)}
                  style={{ ...factChipStyle(confirmed), cursor: "pointer", fontFamily: "inherit",
                    outline: editingKey === fact.key ? "2px solid #2563eb" : "none" }}
                >
                  {body}
                  <span aria-hidden="true" style={{ color: "var(--audit-text-muted)" }}>▾</span>
                </button>
              );
            })}
            {missingEditable.map((entry) => (
              <button
                type="button"
                key={`missing-${entry.fact_key}`}
                disabled={recomputing}
                aria-label={`${factLabel(entry.fact_key)} 선택`}
                aria-expanded={editingKey === entry.fact_key}
                onClick={() => setEditingKey(editingKey === entry.fact_key ? null : entry.fact_key)}
                style={{ ...factChipStyle(false), cursor: "pointer", fontFamily: "inherit",
                  borderStyle: "dashed", color: "var(--audit-text-muted)",
                  outline: editingKey === entry.fact_key ? "2px solid #2563eb" : "none" }}
              >
                <span>{factLabel(entry.fact_key)}</span>
                <b>선택</b>
              </button>
            ))}
          </div>
          {editingEntry && (
            <div style={{ marginTop: 6 }}>
              <div>{editingEntry.prompt_ko || `${factLabel(editingKey)}을(를) 확인해 주세요.`}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 4 }}>
                {editingEntry.options.map((option) => {
                  const value = option && typeof option === "object" ? option.value : option;
                  const selected = String(value) === editingCurrent;
                  return (
                    <button
                      type="button"
                      key={String(value)}
                      disabled={recomputing}
                      aria-pressed={selected}
                      onClick={() => { setEditingKey(null); onAnswer(editingKey, value); }}
                      style={optionButtonStyle(selected)}
                    >
                      {valueLabel(value, editingKey)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}
        </div>
      )}

      {!structured && <ProductFactDialoguePanel key={q} tree={tree} onAnswer={onAnswer} answers={answers} disabled={recomputing} />}

      {!structured && domesticQuestions.length > 0 && (
        <div style={{ marginTop: 9, fontSize: 12, lineHeight: 1.55 }}>
          <b>국내세분 추가 확인</b>
          <ul style={{ margin: "4px 0 0", paddingLeft: 19 }}>
            {domesticQuestions.map((text, index) => <li key={`${text}-${index}`}>{text}</li>)}
          </ul>
        </div>
      )}

      {!structured && deferredCount > 0 && (
        <div style={{ marginTop: 7, fontSize: 11, color: "var(--audit-text-muted)" }}>
          {dialogue.product_intake?.kind === 'food_preparation' ? '확인·수정한 답변에 따라 필요한 질문만 이어집니다.' : `위 사실을 확인하면 이어지는 질문 ${deferredCount}개가 있습니다.`}
        </div>
      )}

      <ResearchFactsBlock
        q={q}
        tree={tree}
        answers={answers}
        recomputing={recomputing}
        displayValue={displayValue}
        onAnswerMany={onAnswerMany}
        onPhase={setResearchPhase}
        onResearchReady={onResearchReady}
        fetchImpl={fetchImpl}
      />

      {!structured && (
        <PrecedentHintsBlock
          hints={precedentHints}
          tree={tree}
          recomputing={recomputing}
          selectedHsk={candidateHsk}
          onSelect={(hsk) => setCandidateHsk(hsk)}
        />
      )}

      {!structured && <PrecedentGuideBlock tree={tree} hints={precedentHints} answers={answers} />}
      <DecisionConditions tree={tree} />

      {!structured && answeredEntries.length > 0 && (
        <div style={{ marginTop: 9, fontSize: 12, lineHeight: 1.55 }}>
          <b>내가 답한 사실</b>
          <span style={{ marginLeft: 6, fontSize: 11, color: "var(--audit-text-muted)" }}>
            이 검색어에 저장되어 다시 계산할 때마다 쓰입니다. ✕로 지우면 그 질문을 다시 묻습니다.
          </span>
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6, marginTop: 5 }}>
            {answeredEntries.map(([key, value]) => (
              <span key={`answer-${key}`} style={factChipStyle(!['unknown','undetermined','edit_required',''].includes(String(value ?? '')))}>
                <span style={{ color: "var(--audit-text-muted)" }}>{factLabel(key)}</span>
                <b title={displayValue(key, value)}>{shortLabel(displayValue(key, value))}</b>
                <button
                  type="button"
                  disabled={recomputing}
                  aria-label={`${factLabel(key)} 답 지우기`}
                  onClick={() => onRemoveAnswer?.(key)}
                  style={{ border: "none", background: "transparent", cursor: "pointer", padding: 0, color: "var(--audit-text-muted)", fontFamily: "inherit", fontSize: 12 }}
                >
                  ✕
                </button>
              </span>
            ))}
          </div>
        </div>
      )}

      {!recomputing && error && (
        <div style={{ marginTop: 8, fontSize: 11, color: "var(--audit-text-muted)", fontWeight: 600 }}>
          다시 계산하지 못했습니다. 이전 결과를 유지합니다.
        </div>
      )}

      <CardFooter onOpenProve={onOpenProve} />
    </div>
  );
}

/**
 * 판정 가이드(PROVE 확정 전). 결정트리 경로·뒷받침 결정례·갈림 결정례와 가르는 사실·신고 전 확인을
 * 결정론적으로 엮어 보여 준다(`precedentGuide.js`). LLM 없이 트리 출력과 결정례 집계만 쓴다.
 */
function PrecedentGuideBlock({ tree, hints, answers }) {
  const guide = buildPrecedentGuide({ tree, hints, answers });
  if (!guide || guide.isEmpty || (!guide.path.current && guide.diverging.length === 0)) return null;
  const rowStyle = { display: "grid", gridTemplateColumns: "88px minmax(0, 1fr)", gap: 8, alignItems: "baseline", marginTop: 5 };
  const keyStyle = { fontSize: 11, fontWeight: 700, color: "var(--audit-text-muted)" };
  return (
    <div data-testid="precedent-guide" style={{ marginTop: 10, padding: "9px 12px", border: "1px solid var(--audit-border)", borderRadius: 7, background: "var(--audit-bg-subtle)", fontSize: 12, lineHeight: 1.55 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <b>판정 가이드</b>
        <span style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>PROVE 확정 전 · 결정트리 경로와 국내 결정례로만 엮은 안내</span>
      </div>
      {guide.path.label && (
        <div style={rowStyle}>
          <span style={keyStyle}>지금 경로</span>
          <span>{guide.path.label}</span>
        </div>
      )}
      {guide.supporting.length > 0 && (
        <div style={rowStyle}>
          <span style={keyStyle}>뒷받침 결정례</span>
          <span>
            같은 경로 <b>{guide.supportingCount}건</b>
            {guide.supporting.slice(0, 3).map((row) => (
              <span key={row.hsk} style={{ display: "block", color: "var(--audit-text-muted)", fontSize: 11 }}>
                {formatSubdivisionHsk(row.hsk)} · {row.count}건{row.latest_date ? ` · 최근 ${row.latest_date}` : ""}{row.label ? ` · ${shortLabel(row.label, 60)}` : ""}
              </span>
            ))}
          </span>
        </div>
      )}
      {guide.diverging.length > 0 && (
        <div style={rowStyle}>
          <span style={keyStyle}>갈림 결정례</span>
          <span>
            {guide.diverging.map((d) => (
              <span key={d.code} style={{ display: "block" }}>
                <b>{d.display}</b>{d.name ? ` · ${shortLabel(d.name, 40)}` : ""} · {d.count}건{d.latest_date ? ` · 최근 ${d.latest_date}` : ""}
                {d.samples[0] ? <span style={{ color: "var(--audit-text-muted)" }}> · {shortLabel(d.samples[0], 60)}</span> : null}
              </span>
            ))}
            {guide.decidingFact && (
              <span style={{ display: "block", marginTop: 3 }}>
                가르는 사실: <b>{guide.decidingFact.prompt}</b>
                {guide.decidingFact.reason === "first_open" && (
                  <span style={{ color: "var(--audit-text-muted)" }}> (아직 답하지 않은 첫 질문)</span>
                )}
              </span>
            )}
          </span>
        </div>
      )}
      {guide.checklist.length > 0 && (
        <div style={rowStyle}>
          <span style={keyStyle}>신고 전 확인</span>
          <ul style={{ margin: 0, paddingLeft: 18 }}>
            {guide.checklist.map((item, index) => (
              <li key={`${item.kind}-${item.key || item.code || index}`}>
                {item.kind === "legal" ? "법적 확인: " : item.kind === "fact" ? "사실 확인: " : ""}{item.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

/**
 * AI 웹검증(제조사 공식 자료 등) 요약을 현재 트리가 묻는 축에 투영해 '제안'으로 보여 주고, 사용자가
 * 확인한 값만 답으로 저장한다. 제품을 모르는 사용자가 질문을 하나씩 고르지 않아도 되게 하는 경로다.
 * 웹검증 캐시가 없으면 같은 상품 조사를 한 번 실행한다. 제안은 원문 근거 문장이 있는 값뿐이다.
 */
function ResearchFactsBlock({ q, tree, answers, recomputing, displayValue, onAnswerMany, onPhase, onResearchReady, fetchImpl }) {
  const [state, setState] = useState({ q: null, status: "idle", proposals: [], conflicts: [], reason: "", dismissed: {} });
  const [autoOn, setAutoOn] = useState(() => researchAutoEnabled());
  const openQuestions = productDialogue(tree).questions;
  const editable = (Array.isArray(tree?.editable_facts) ? tree.editable_facts : []).filter(isProductQuestion);
  const action = String(tree?.action || "");
  const resolved = action === "supported" && tree?.supported_hsk;
  const directIntake = ['juice','milling','food_preparation'].includes(tree?.product_fact_dialogue?.product_intake?.kind);
  const hidden = directIntake || (openQuestions.length === 0 && editable.length === 0) || Boolean(resolved);
  const loading = state.q === q && (state.status === "loading_research" || state.status === "loading_project");
  const stateRef = useRef(state);
  stateRef.current = state;
  useEffect(() => {
    onPhase?.(state.q === q ? state.status : "idle");
  }, [state.q, state.status, q, onPhase]);

  const run = async () => {
    if (directIntake) return;
    markResearchAutoDone(q);
    setState({ q, status: "loading_research", proposals: [], conflicts: [], reason: "", dismissed: {} });
    try {
      const research = await loadProductResearch(q);
      setState((s) => (s.q === q ? { ...s, status: "loading_project" } : s));
      const summary = researchSummaryText(research);
      if (research) onResearchReady?.(research);
      if (!summary) {
        setState({ q, status: "empty", proposals: [], conflicts: [], reason: "no_research", dismissed: {} });
        return;
      }
      const token = localStorage.getItem("token");
      const result = await fetchResearchFactProposals({
        query: q, summary, identity: research?.identity, shadow: tree, token, fetchImpl,
      });
      setState({
        q, status: result.proposals.length ? "proposed" : "empty",
        proposals: result.proposals, conflicts: result.conflictingKeys || [], reason: result.reason, dismissed: {},
      });
    } catch (error) {
      setState({ q, status: "error", proposals: [], conflicts: [], reason: String(error?.message || error), dismissed: {} });
    }
  };

  // 미확정 사전 점검이면 검색어당 한 번(탭 세션) 자동으로 웹검증 투영을 돌린다. 답이 이미 있거나,
  // AI 웹검증이 이 검색어의 제품을 찾지 못한 상태(pass)면 돌리지 않는다. 제안은 여전히 사용자가 확인한다.
  const unresolved = !hidden && (action === "ask" || action === "ambiguous") && openQuestions.length > 0;
  const noAnswers = !answers || Object.keys(answers).length === 0;
  useEffect(() => {
    if (!q || !unresolved || !noAnswers || !autoOn || recomputing) return;
    if (state.q === q || researchAutoDone(q)) return;
    const gate = readGateState(q);
    if (gate && gate.phase === "pass") return;
    run();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, unresolved, noAnswers, autoOn]);

  // AI 웹검증 블록(또는 다른 탭)이 같은 검색어의 조사를 끝내면, 카드가 아직 제안을 못 받은 상태일 때
  // 같은 캐시로 투영만 다시 한다(조사 재실행 없음). 자기 조사 중에는 무시한다.
  useEffect(() => {
    if (!q || hidden) return undefined;
    const key = researchEventKey(q);
    const onUpdated = (event) => {
      if (event?.detail?.key !== key) return;
      const current = stateRef.current;
      const busy = current.q === q && (current.status === "loading_research" || current.status === "loading_project");
      const settled = current.q === q && current.status === "proposed";
      if (busy || settled) return;
      const gate = readGateState(q);
      if (!gate || !gate.research) return;
      run();
    };
    window.addEventListener(RESEARCH_UPDATED_EVENT, onUpdated);
    return () => window.removeEventListener(RESEARCH_UPDATED_EVENT, onUpdated);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q, hidden]);

  if (hidden) return null;

  const active = state.q === q;
  // 방호·차폐복이 제안되거나 이미 확인됐으면 의류 종류 제안(스커트·조끼 등)은 숨긴다. 방호복은 트리가
  // "그 밖의 의류"로 계산하므로 그 제안을 확인하면 오히려 스커트 호로 경로가 바뀐다.
  const protectiveProposed = active && (String(answers?.protective_apparel_status ?? "") === "true"
    || state.proposals.some((p) => p.fact_key === "protective_apparel_status" && String(p.value) === "true" && !state.dismissed[p.fact_key]));
  const pending = active ? state.proposals.filter((p) => !requiresDirectProductAnswer(p.fact_key) && !state.dismissed[p.fact_key]
    && !(protectiveProposed && p.fact_key === "garment_type")
    && String(answers?.[p.fact_key] ?? "") !== String(p.value)) : [];
  const confirm = (list) => {
    const entries = {};
    list.forEach((p) => { if (!requiresDirectProductAnswer(p.fact_key)) entries[p.fact_key] = p.value; });
    onAnswerMany?.(entries);
  };
  const excerpt = (span) => {
    const text = String(span || "");
    return text.length > 90 ? `${text.slice(0, 90)}…` : text;
  };

  const conflictLabels = active ? (state.conflicts || []).map((key) => factLabel(key)) : [];

  return (
    <div data-testid="research-facts-block" style={{ marginTop: 9, fontSize: 12, lineHeight: 1.55 }}>
      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          type="button"
          disabled={recomputing || loading}
          onClick={run}
          style={optionButtonStyle(false)}
        >
          {loading
            ? (state.status === "loading_research" ? "제품을 조사하는 중…" : "질문에 맞추는 중…")
            : (active && state.status !== "idle" ? "웹검증 결과로 다시 채우기" : "웹검증 결과로 사실 채우기")}
        </button>
        <span style={{ fontSize: 11, color: "var(--audit-text-muted)" }}>
          AI 웹검증이 찾은 제품 설명을 위 질문에 맞춰 제안합니다. 확인한 값만 답으로 저장됩니다.
        </span>
        <label style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11, color: "var(--audit-text-muted)", cursor: "pointer" }}>
          <input
            type="checkbox"
            checked={autoOn}
            onChange={(e) => { setAutoOn(e.target.checked); setResearchAutoEnabled(e.target.checked); }}
            aria-label="미확정이면 웹검증 투영 자동 실행"
          />
          미확정이면 자동 실행
        </label>
      </div>
      {loading && (
        <div role="status" style={{ marginTop: 5, fontSize: 11, color: "var(--audit-text-muted)" }}>
          {state.status === "loading_research"
            ? "AI 웹검증이 제조사·공식 자료에서 제품 설명을 만들고 있습니다…"
            : "제품 설명을 위 질문의 선택지에 맞추고 있습니다…"}
        </div>
      )}
      {active && !loading && conflictLabels.length > 0 && (
        <div data-testid="research-facts-conflicts" style={{ marginTop: 5, fontSize: 11, color: "var(--audit-text-muted)", fontWeight: 600 }}>
          웹검증 설명에서 값이 갈리는 축: <b>{conflictLabels.join(" · ")}</b> — 제안하지 않았습니다. 위 질문에서 직접 골라 주세요.
        </div>
      )}
      {active && state.status === "empty" && (
        <div style={{ marginTop: 5, fontSize: 11, color: "var(--audit-text-muted)" }}>
          {state.reason === "no_research"
            ? "AI 웹검증이 이 검색어의 제품 설명을 찾지 못했습니다. 전체 탭의 AI 웹검증에서 제품을 특정한 뒤 다시 시도하세요."
            : "웹검증 설명에서 위 질문에 바로 대응하는 근거 문장을 찾지 못했습니다. 질문에 직접 답해 주세요."}
        </div>
      )}
      {active && state.status === "error" && (
        <div style={{ marginTop: 5, fontSize: 11, color: "var(--audit-text-muted)", fontWeight: 600 }}>
          웹검증 결과를 가져오지 못했습니다. 질문에 직접 답해 주세요.
        </div>
      )}
      {active && state.status === "proposed" && pending.length === 0 && (
        <div style={{ marginTop: 5, fontSize: 11, color: "var(--audit-text-muted)" }}>
          웹검증 제안을 모두 반영했거나 제외했습니다.
        </div>
      )}
      {pending.length > 0 && (
        <div style={{ marginTop: 6 }}>
          <b>웹검증이 찾은 사실</b>
          <span style={{ marginLeft: 6, fontSize: 11, color: "var(--audit-text-muted)" }}>
            제안 · 확인 전에는 판정에 쓰이지 않습니다
          </span>
          <div style={{ display: "flex", flexDirection: "column", gap: 5, marginTop: 5 }}>
            {pending.map((p) => (
              <div key={`proposal-${p.fact_key}`} style={{ display: "flex", alignItems: "flex-start", gap: 8, flexWrap: "wrap" }}>
                <span style={factChipStyle(false)}>
                  <span style={{ color: "var(--audit-text-muted)" }}>{factLabel(p.fact_key)}</span>
                  <b title={displayValue(p.fact_key, p.value)}>{shortLabel(displayValue(p.fact_key, p.value))}</b>
                </span>
                {p.source_span && (
                  <span style={{ fontSize: 11, color: "var(--audit-text-muted)", flex: "1 1 240px" }}>
                    근거: “{excerpt(p.source_span)}”
                  </span>
                )}
                <span style={{ display: "inline-flex", gap: 4 }}>
                  <button
                    type="button"
                    disabled={recomputing}
                    onClick={() => confirm([p])}
                    style={optionButtonStyle(false)}
                    aria-label={`${factLabel(p.fact_key)} 제안 확인`}
                  >
                    ✓ 확인
                  </button>
                  <button
                    type="button"
                    disabled={recomputing}
                    onClick={() => setState((s) => ({ ...s, dismissed: { ...s.dismissed, [p.fact_key]: true } }))}
                    style={optionButtonStyle(false)}
                    aria-label={`${factLabel(p.fact_key)} 제안 제외`}
                  >
                    ✕
                  </button>
                </span>
              </div>
            ))}
          </div>
          {pending.length > 1 && (
            <button
              type="button"
              disabled={recomputing}
              onClick={() => confirm(pending)}
              style={{ ...optionButtonStyle(true), marginTop: 6 }}
            >
              모두 확인 ({pending.length})
            </button>
          )}
        </div>
      )}
    </div>
  );
}

const PROGRESS_KEYFRAMES = `
@keyframes loginhs-preview-spin { to { transform: rotate(360deg); } }
@keyframes loginhs-preview-pulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.35; } }
`;

/**
 * 사용자가 지금 무엇을 기다리는지 보이게 하는 진행 카드. 단계 목록(완료 ✓ · 진행 중 ● · 대기 ○)과
 * 경과 시간을 보여 준다. 초기 계산·웹검증 조사·투영·재계산에 같은 모양을 쓴다.
 */
function ProgressCard({ title, detail, steps, inner = false }) {
  const [startedAt] = useState(() => Date.now());
  const [elapsed, setElapsed] = useState(0);
  useEffect(() => {
    const timer = setInterval(() => setElapsed(Math.floor((Date.now() - startedAt) / 1000)), 1000);
    return () => clearInterval(timer);
  }, [startedAt]);
  return (
    <div
      role="status"
      aria-live="polite"
      data-testid="preview-progress"
      style={{
        ...(inner ? {} : cardStyle),
        border: "1px solid #bfdbfe",
        borderLeft: "4px solid #93c5fd",
        borderRadius: 7,
        padding: "10px 14px",
        marginTop: inner ? 8 : 0,
        marginBottom: inner ? 8 : 12,
        background: "var(--audit-bg-elevated, var(--audit-bg))",
      }}
    >
      <style>{PROGRESS_KEYFRAMES}</style>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <span
          aria-hidden="true"
          style={{
            width: 16, height: 16, borderRadius: "50%", flex: "0 0 auto",
            border: "2px solid #bfdbfe", borderTopColor: "#2563eb",
            animation: "loginhs-preview-spin 0.9s linear infinite",
          }}
        />
        <strong style={{ fontSize: 13 }}>{title}</strong>
        <span style={{ marginLeft: "auto", fontSize: 11, color: "var(--audit-text-muted)", fontVariantNumeric: "tabular-nums" }}>
          {elapsed}초 경과
        </span>
      </div>
      {detail && (
        <div style={{ marginTop: 4, fontSize: 12, color: "var(--audit-text-muted)", lineHeight: 1.5 }}>{detail}</div>
      )}
      {Array.isArray(steps) && steps.length > 0 && (
        <ol style={{ display: "flex", flexWrap: "wrap", gap: 6, listStyle: "none", margin: "8px 0 0", padding: 0 }}>
          {steps.map((step, index) => (
            <li
              key={`${step.label}-${index}`}
              data-state={step.state}
              style={{
                display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11,
                padding: "2px 9px", borderRadius: 999,
                border: `1px solid ${step.state === "active" ? "#93c5fd" : "var(--audit-border)"}`,
                background: step.state === "active" ? "#eff6ff" : step.state === "done" ? "var(--audit-bg-subtle)" : "transparent",
                color: step.state === "pending" ? "var(--audit-text-muted)" : "inherit",
                fontWeight: step.state === "active" ? 600 : 400,
                animation: step.state === "active" ? "loginhs-preview-pulse 1.4s ease-in-out infinite" : "none",
              }}
            >
              <span aria-hidden="true">{step.state === "done" ? "✓" : step.state === "active" ? "●" : "○"}</span>
              {index + 1}. {step.label}
            </li>
          ))}
        </ol>
      )}
    </div>
  );
}

function CardHeader({ title, meta }) {
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
      <strong style={{ fontSize: 13 }}>{title}</strong>
      <span style={draftBadgeStyle}>PROVE 확정 전</span>
      <span
        style={{
          marginLeft: "auto",
          borderRadius: 999,
          padding: "2px 8px",
          fontSize: 11,
          fontWeight: 700,
          color: meta.color,
          background: meta.background,
        }}
      >
        {meta.label}
      </span>
    </div>
  );
}

function CardFooter({ onOpenProve }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        gap: 8,
        flexWrap: "wrap",
        marginTop: 9,
        paddingTop: 8,
        borderTop: "1px solid var(--audit-border)",
        fontSize: 11,
        color: "var(--audit-text-muted)",
      }}
    >
      <span>근거 사실이 부족하면 세번을 확정하지 않습니다.</span>
      {onOpenProve && (
        <button type="button" onClick={onOpenProve} style={openButtonStyle}>
          확정 검토 · 보고서
        </button>
      )}
    </div>
  );
}

function ResultCell({ label, value, accent }) {
  return (
    <div
      style={{
        border: "1px solid var(--audit-border)",
        borderRadius: 5,
        padding: "7px 9px",
        background: "var(--audit-bg-subtle, rgba(148,163,184,.08))",
      }}
    >
      <div style={{ fontSize: 11, color: "var(--audit-text-muted)", marginBottom: 3 }}>{label}</div>
      <div
        style={{
          fontFamily: "JetBrains Mono, monospace",
          fontSize: 14,
          fontWeight: 700,
          color: accent ? "var(--audit-accent, #2563eb)" : "var(--audit-text)",
        }}
      >
        {value}
      </div>
    </div>
  );
}

const cardStyle = {
  border: "1px solid var(--audit-border)",
  borderRadius: 7,
  padding: "12px 14px",
  marginBottom: 12,
  background: "var(--audit-bg-elevated, var(--audit-bg))",
};


const gridStyle = {
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(210px, 1fr))",
  gap: 8,
  marginTop: 10,
};

const draftBadgeStyle = {
  border: "1px solid var(--audit-border)",
  borderRadius: 999,
  padding: "1px 7px",
  fontSize: 11,
  fontWeight: 600,
  color: "var(--audit-text-muted)",
};

const openButtonStyle = {
  border: "1px solid var(--audit-border)",
  borderRadius: 4,
  padding: "3px 8px",
  background: "transparent",
  color: "var(--audit-accent)",
  fontFamily: "inherit",
  fontSize: 11,
  cursor: "pointer",
};

const factChipStyle = (confirmed) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 5,
  border: `1px solid ${confirmed ? "#93c5fd" : "var(--audit-border)"}`,
  borderRadius: 999,
  padding: "2px 9px",
  fontSize: 11,
  background: confirmed ? "#eff6ff" : "var(--audit-bg-subtle, rgba(148,163,184,.08))",
});

const optionButtonStyle = (selected) => ({
  border: `1px solid ${selected ? "#2563eb" : "var(--audit-border)"}`,
  borderRadius: 999,
  padding: "3px 10px",
  fontFamily: "inherit",
  fontSize: 11,
  cursor: "pointer",
  color: selected ? "#1d4ed8" : "var(--audit-text)",
  background: selected ? "#dbeafe" : "transparent",
  fontWeight: selected ? 700 : 500,
});

/**
 * 후보 검수 기록 — 앞단 후보마다 트리가 받았는지(열림·거절·트리 없음), 검색어가 그 류를 가리키는지, PROVE 검수가 무엇을 골랐는지.
 * 서버 candidate_route.screening[]·screening_pick 을 그대로 보인다(판정 아님).
 */
export function CandidateScreening({ route }) {
  const rows = Array.isArray(route?.screening) ? route.screening : [];
  const judge = route?.screening_pick;
  if (!rows.length && !judge) return null;
  const status = { open: "트리가 받음", rejected: "트리가 거절(범위 밖)", no_tree: "맡을 트리 없음" };
  const support = { head: "검색어 물품명이 이 류", body: "검색어에 이 류 낱말", none: "" };
  return (
    <div data-testid="candidate-screening" style={{ flexBasis: "100%", fontSize: 11, color: "var(--audit-text-muted)", lineHeight: 1.7 }}>
      <span style={{ fontWeight: 700 }}>후보 검수</span>
      {rows.map((s) => (
        <span key={`${s.hs4}-${s.source}`} style={{ marginLeft: 8 }}>
          <b style={{ fontFamily: "JetBrains Mono, monospace", color: s.chosen ? "#1d4ed8" : undefined }}>{s.hs4}</b>
          ({candidateSourceLabel(s.source)} · {status[s.status] || s.status}{support[s.text_support] ? ` · ${support[s.text_support]}` : ""}){s.chosen ? " ← 열림" : ""}
        </span>
      ))}
      {judge && (
        <span data-testid="candidate-judge" style={{ marginLeft: 8 }}>
          · PROVE 판정 <b style={{ fontFamily: "JetBrains Mono, monospace" }}>{judge.hs4}</b> 확신 {Math.round((Number(judge.confidence) || 0) * 100)}%
          {judge.used ? " → 이 호로 먼저 열었습니다" : " → 확신이 낮아 순서는 그대로"}
        </span>
      )}
    </div>
  );
}

/** 사용자가 직접 고른 가정(다른 후보 호·PROVE 소호 제안) — 자동 신호가 사라져도 유지하고, 계산 못 하면 안내만 한다. */
function userChosenBasis(basis) {
  return basis === "user_alternative" || basis === "prove_subheading";
}

function formatHs6(hs6) {
  const d = String(hs6 || "").replace(/\D/g, "");
  return d.length >= 6 ? `${d.slice(0, 4)}.${d.slice(4, 6)}` : d;
}

/**
 * PROVE 소호 제안(2026-09-28) — 후보 호로 연 트리가 소호를 아직 못 정했을 때 서버가 싣는 제안 한 줄.
 * 제안은 답이 아니다: 누르면 그 소호로 가정해 다시 계산하고(조건부·확정 아님), 누르지 않으면 아무것도 바뀌지 않는다.
 */
export function SubheadingProposal({ proposal, disabled = false, onApply = null }) {
  if (!proposal || !proposal.hs6) return null;
  const pct = Math.round((Number(proposal.confidence) || 0) * 100);
  return (
    <div data-testid="subheading-proposal" style={{ flexBasis: "100%", fontSize: 12, lineHeight: 1.6, color: "var(--audit-text-muted)" }}>
      <span style={{ fontWeight: 700 }}>PROVE 소호 제안</span>{" "}
      <b style={{ fontFamily: "JetBrains Mono, monospace", color: "#1d4ed8" }}>{formatHs6(proposal.hs6)}</b>
      {proposal.label ? <> {proposal.label}</> : null}
      <span> · 확신 {pct}% · 제안일 뿐 답이 아닙니다</span>
      {onApply && (
        <button type="button" data-testid="subheading-proposal-apply" disabled={disabled} onClick={() => onApply(proposal)}
          style={{ marginLeft: 8, border: "1px solid var(--audit-border)", borderRadius: 999, padding: "0 10px", fontSize: 11, background: "transparent", color: "#1d4ed8", cursor: disabled ? "default" : "pointer", fontFamily: "inherit" }}>
          이 소호로 계산
        </button>
      )}
    </div>
  );
}

/** 후보 호 힌트 출처 라벨 — 서버 candidate_route.source. */
function candidateSourceLabel(source) {
  return { tariff: "관세율표 명칭 일치", tariff_llm: "LLM 품명의 관세율표 명칭 일치", precedent: "국내 결정례", history: "우리 사무소 이력", llm: "LLM 후보" }[String(source || "")] || "제안 신호";
}
